import { renderOnYourMind, renderUpdateModal, renderPlanStatus } from "./companionUpdate";
import { state } from "./state";
import { renderPortraitView, renderPortraitClaimModal, renderPortraitNotQuiteDialog } from "./portraitView";
import { renderPortraitMatchingSection } from "./portraitMatching";
import { morph } from "./morph";
import { escapeHtml, toRelativeDate, shortUid, displayNameForProfile, displayAuthor, timestampToDate } from "./utils";
import { APP_STORE_URL, DAILY_PROMPT_FALLBACK, PRIMARY_NAV_TABS, PRIMARY_TAB_FOR, TAB_LABELS } from "./constants";
import { pathForTab } from "./routing";
import { usernameCache, containsObjectionableContent } from "./services";
import { Capacitor } from "@capacitor/core";
import type { SharedPost, CommunityPost, PublicProfile, Reply, SimilarThought, TabName, ThreadInsight, ThinkingPattern, ThinkingProfileSnapshot, FriendsTab, CompanionMode, SupportPattern, DiscoveryDirection } from "./types";

// ── Helpers ──────────────────────────────────────────────────────────────
function isBusy(key: string): boolean { return state.busy.has(key); }

function isNativeAppShell(): boolean {
  return document.documentElement.classList.contains("capacitor-native") || Capacitor.isNativePlatform();
}

function isPhoneTabShell(): boolean {
  return isNativeAppShell() || window.matchMedia("(max-width: 768px)").matches;
}

function nativeDeviceLabel(): string {
  return isNativeAppShell() ? "phone" : "browser";
}

function nativePlatformList(): string {
  return isNativeAppShell() ? "Android, iPhone, and web" : "iPhone and web";
}

function encryptionGateCopy(): { title: string; message: string; button: string } {
  const deviceLabel = nativeDeviceLabel();
  if (!state.noteEncryption.hasEnvelope) {
    return {
      title: "Set up note encryption",
      message: `Create one passphrase before writing on this ${deviceLabel}. It unlocks your notes across Android, iPhone, and web.`,
      button: "Set up",
    };
  }
  return {
    title: "Unlock note encryption",
    message: `Enter your passphrase once before writing on this ${deviceLabel}.`,
    button: "Unlock",
  };
}

function isOwnUid(uid: string | undefined): boolean {
  return !!(uid && state.user?.uid && uid === state.user.uid);
}

function matchReasonText(reason: string | undefined): string {
  const trimmed = String(reason || "").trim();
  if (/^shared tension:/i.test(trimmed) || /^shared theme:/i.test(trimmed)) return trimmed;
  if (/^similar tension:/i.test(trimmed)) {
    const value = trimmed.replace(/^similar tension:/i, "").trim();
    return value ? `Shared tension: ${value}` : "A similar line of thinking";
  }
  return "A similar line of thinking";
}

// Presence over popularity: never surface numeric social counts publicly.
function socialProofText(replyCount: number | undefined): string {
  const count = Number(replyCount || 0);
  if (count === 1) return "Someone continued this thought";
  if (count > 1) return "People are continuing this thought";
  return "";
}

// Own-post resonance shown as presence, not a number.
/// The writer's private stats — identical numbers and gating to iOS:
/// "N felt · N seen" (uniqueViewerCount), each segment only when > 0,
/// nothing at all while both are zero.
function ownerStatsPill(post: SharedPost): string {
  const felt = Math.max(Number(state.resonanceCountOverrides[post.id] ?? post.resonanceCount ?? 0), 0);
  const seen = Math.max(Number(post.uniqueViewerCount ?? 0), 0);
  if (felt <= 0 && seen <= 0) return "";
  const parts: string[] = [];
  if (felt > 0) parts.push(`<span class="felt-heart">♥</span> ${felt} felt`);
  if (seen > 0) parts.push(`<span class="seen-eye">👁</span> ${seen} seen`);
  return `<span class="felt-readonly owner-stats" title="Only you can see this">${parts.join(`<span class="owner-stats-sep"> · </span>`)}</span>`;
}

function ownResonancePresenceText(resonanceCount: number): string {
  if (resonanceCount === 1) return "Someone felt this";
  if (resonanceCount > 1) return "Others felt this";
  return "";
}

function echoHeadline(daysApart: number | undefined, sharedTension: string | undefined): string {
  const days = Number(daysApart || 0);
  if (days >= 1) return `${days} days later, same tension`;
  if (String(sharedTension || "").trim()) return "This keeps coming back";
  return "You've thought about this before";
}

function profileAvatarHTML(url: string | undefined, label: string, className: string): string {
  const initial = escapeHtml((label || "L").slice(0, 1).toUpperCase());
  if (url) {
    return `<img class="${className}" src="${escapeHtml(url)}" alt="" />`;
  }
  return `<div class="${className} avatar-fallback" aria-hidden="true">${initial}</div>`;
}

type FriendRowPerson = {
  uid: string;
  username: string;
  displayName?: string;
  bio?: string;
  avatarURL?: string;
};

function renderPersonName(person: FriendRowPerson): string {
  const username = person.username ? `@${person.username}` : "";
  const displayName = String(person.displayName || "").trim();
  if (!displayName) {
    return `<div class="friend-row-title">${escapeHtml(username || "LOT member")}</div>`;
  }
  return `
    <div class="friend-row-title">${escapeHtml(displayName)}</div>
    ${username ? `<div class="friend-row-handle">${escapeHtml(username)}</div>` : ""}
  `;
}

function renderAuthorMeta(post: SharedPost | CommunityPost): string {
  const base = escapeHtml(displayAuthor(post, usernameCache, state.user?.uid));
  if (post.attribution === "username" && post.authorUid) {
    const label = isOwnUid(post.authorUid) ? `${base} <span class="you-tag">you</span>` : base;
    return `<button class="author-link" data-action="open-public-profile" data-uid="${escapeHtml(post.authorUid)}">${label}</button>`;
  }
  const anonymousColorIndex = Number(("anonymousColorIndex" in post ? post.anonymousColorIndex : 0) || 0);
  return `
    <span class="anonymous-id">
      <span class="anonymous-dot anonymous-dot-${anonymousColorIndex % 8}"></span>
      <span>${base}</span>
    </span>
  `;
}

function sortedReplies(replies: Reply[]): Reply[] {
  return [...replies].sort((a, b) => {
    const pathA = a.path || a.id;
    const pathB = b.path || b.id;
    if (pathA === pathB) {
      return timestampToDate(a.createdAt).getTime() - timestampToDate(b.createdAt).getTime();
    }
    return pathA.localeCompare(pathB);
  });
}

function toLocalDatetimeInput(dateRaw: unknown): string {
  const date = timestampToDate(dateRaw as Date);
  const pad = (v: number) => String(v).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function activityTitle(item: typeof state.activity[0]): string {
  switch (item.type) {
    case "friend_request":   return "Friend request";
    case "friend_post":      return "Friend posted";
    case "community_invite": return "Community invite";
    case "community_post":   return "Community post";
    case "thought_reply":    return "Response on your thought";
    case "community_reply":  return "Response in your community";
    case "resonance_tap":
    case "resonance_reaction": return "Someone felt this";
    default:                 return item.type || "Activity";
  }
}

function activityMessage(item: typeof state.activity[0]): string {
  if (item.body) return item.body;
  switch (item.type) {
    case "friend_request":   return "Someone sent you a friend request.";
    case "friend_post":      return "A friend shared a thought.";
    case "community_invite": return "You were invited to a community.";
    case "resonance_tap":
    case "resonance_reaction": return "Someone resonated with your thought.";
    default:                 return "Open to view details.";
  }
}

function reflectionSummary(reflection: ThreadInsight["reflection"] | null | undefined): string {
  if (!reflection) return "";
  const summary = String(reflection.summary || "").trim();
  if (summary) return summary;
  const tension = String(reflection.tension || "").trim();
  if (tension) return `This thought circles ${tension}.`;
  const firstTheme = Array.isArray(reflection.themes) ? String(reflection.themes[0] || "").trim() : "";
  if (firstTheme) return `This thought keeps returning to ${firstTheme}.`;
  return String(reflection.logic || "").trim();
}

const companionModes: Array<{ id: CompanionMode; label: string; helper: string }> = [
  { id: "reflect", label: "Reflect", helper: "Name what it notices" },
  { id: "clarify", label: "Clarify", helper: "Make the thought cleaner" },
  { id: "ground", label: "Ground me", helper: "Slow the moment down" },
  { id: "pattern", label: "Find pattern", helper: "Connect the repeats" },
  { id: "question", label: "One question", helper: "Ask only one thing" },
];

function supportPatternCopy(pattern: SupportPattern | null | undefined): string {
  const label = String(pattern?.label || "").trim();
  if (!label || label.toLowerCase() === "unclear") return "";
  const copy = String(pattern?.copy || "").trim();
  return copy ? `${label} — ${copy}` : label;
}

function supportPatternPill(pattern: SupportPattern | null | undefined): string {
  const copy = supportPatternCopy(pattern);
  if (!copy) return "";
  return `<div class="support-pattern-pill"><span>A possible thread</span><strong>${escapeHtml(copy)}</strong></div>`;
}

function companionDashboardCopy(): { title: string; body: string } {
  const waitingQuestion = state.companionContext.memories
    .map((memory) => String(memory.nextQuestion || "").trim())
    .find(Boolean);
  if (waitingQuestion) {
    return {
      title: "A question is waiting",
      body: waitingQuestion,
    };
  }
  const topPattern = activePatterns()[0];
  if (topPattern?.entryCount && topPattern.entryCount >= 2) {
    return {
      title: "Something keeps returning",
      body: `${topPattern.entryCount} thoughts around ${topPattern.title.toLowerCase()}.`,
    };
  }
  if (state.notes.length > 0) {
    return {
      title: "Talk through what’s on your mind",
      body: "Start anywhere. Companion can stay conversational or go deeper when you want.",
    };
  }
  return {
    title: "How are you actually doing today?",
    body: "You do not need a finished thought. Just begin where you are.",
  };
}

function renderCompanionDashboardCard(): string {
  const copy = companionDashboardCopy();
  return `
    <button class="companion-dashboard-card" data-action="open-standalone-companion">
      <span class="companion-dashboard-orb" aria-hidden="true">
        <img src="/assets/Companion_LOT.svg" alt="" />
      </span>
      <span class="companion-dashboard-copy">
        <strong>Companion <em>&middot; Private conversation</em></strong>
        <b>${escapeHtml(copy.title)}</b>
        <span>${escapeHtml(copy.body)}</span>
      </span>
      <span class="companion-open-arrow" aria-hidden="true">›</span>
    </button>
  `;
}

function companionPortraitLines(summary: string): string[] {
  return String(summary || "")
    .split(/\n+/)
    .map((line) => line
      .replace(/^(public|private|friends)\s*(\[[^\]]+\])?\s*:\s*/i, "")
      .replace(/^remembered\s*:\s*/i, "")
      .trim())
    .filter(Boolean)
    .slice(0, 8);
}

function noteFallbackSimilarThoughts(noteId: string, noteText: string): SimilarThought[] {
  const existing = state.noteInsights[noteId]?.similarThoughts || [];
  if (existing.length > 0) return existing;

  const tokens = new Set(
    String(noteText || "")
      .toLowerCase()
      .split(/[^a-z0-9]+/i)
      .map((token) => token.trim())
      .filter((token) => token.length >= 4),
  );

  const candidatePool = state.feedPosts.length > 0
    ? state.feedPosts
        .filter((post) => String(post.visibility || "public") === "public" && post.text.trim().length > 0)
        .map((post) => ({
          postId: post.id,
          textPreview: post.text.slice(0, 140),
          replyCount: post.replyCount || 0,
          createdAt: timestampToDate(post.sharedAt || post.createdAt),
        }))
    : state.publicThoughtPool;

  const ranked = candidatePool
    .filter((post) => String(post.textPreview || "").trim().length > 0)
    .map((post) => {
      const postTokens = new Set(
        post.textPreview
          .toLowerCase()
          .split(/[^a-z0-9]+/i)
          .map((token) => token.trim())
          .filter((token) => token.length >= 4),
      );
      let overlap = 0;
      tokens.forEach((token) => {
        if (postTokens.has(token)) overlap += 1;
      });
      const createdAt = timestampToDate(post.createdAt);
      const recencyDays = Math.max(0, (Date.now() - createdAt.getTime()) / 86400000);
      const recencyScore = 1 / (1 + recencyDays / 10);
      const conversationScore = Math.min((post.replyCount || 0) / 8, 1);
      const score = overlap * 0.22 + conversationScore * 0.12 + recencyScore * 0.08;
      return {
        postId: post.postId,
        scope: "sharedPost",
        textPreview: post.textPreview.slice(0, 140),
        replyCount: post.replyCount || 0,
        matchReason: overlap > 0 ? "Related public thought" : "Recent public thought",
        score,
      };
    })
    .filter((thought) => !((state.aiPrefs.hiddenSimilarThoughtPostIds || []).includes(thought.postId || "")))
    .sort((a, b) => {
      const leftHelpful = (state.aiPrefs.helpfulSimilarThoughtPostIds || []).includes(a.postId || "");
      const rightHelpful = (state.aiPrefs.helpfulSimilarThoughtPostIds || []).includes(b.postId || "");
      if (leftHelpful !== rightHelpful) return leftHelpful ? -1 : 1;
      return b.score - a.score;
    })
    .slice(0, 3)
    .map(({ score: _score, ...thought }) => thought);

  return ranked;
}

function similarThoughtFeedbackState(postId: string): "helpful" | "hidden" | "none" {
  if ((state.aiPrefs.hiddenSimilarThoughtPostIds || []).includes(postId)) return "hidden";
  if ((state.aiPrefs.helpfulSimilarThoughtPostIds || []).includes(postId)) return "helpful";
  return "none";
}

function renderSimilarThoughtFeedback(postId: string): string {
  if (!postId) return "";
  const status = similarThoughtFeedbackState(postId);
  return `
    <div class="similar-thought-feedback">
      <button class="btn-ghost btn-xs ${status === "helpful" ? "is-active" : ""}" data-action="similar-thought-helpful" data-post-id="${escapeHtml(postId)}">Helpful</button>
      <button class="btn-ghost btn-xs ${status === "hidden" ? "is-active" : ""}" data-action="similar-thought-hide" data-post-id="${escapeHtml(postId)}">Not relevant</button>
    </div>
  `;
}

function inlineSimilarNoteKey(noteId: string): string {
  return `note:${noteId}`;
}

function similarThoughtOpenKey(thought: SimilarThought): string {
  return `${thought.scope || "sharedPost"}:${thought.groupId || ""}:${thought.postId || ""}`;
}

function renderSimilarThoughtItem(thought: SimilarThought, options: { showFeedback?: boolean } = {}): string {
  const scope = thought.scope || "sharedPost";
  const postId = thought.postId || "";
  const groupId = thought.groupId || "";
  const openKey = similarThoughtOpenKey({ ...thought, scope, postId, groupId });
  const isOpening = !!postId && state.openingSimilarThoughtId === openKey;
  const proof = socialProofText(thought.replyCount);

  return `
    <article class="similar-thought-card">
      <div class="similar-thought-copy">${escapeHtml(thought.textPreview || "")}</div>
      <div class="meta similar-thought-reason">${escapeHtml(matchReasonText(thought.matchReason))}</div>
      ${proof ? `<div class="meta resonance-proof">${escapeHtml(proof)}</div>` : ""}
      <div class="similar-thought-row">
        <button
          class="similar-thought-link"
          data-action="open-similar-thought"
          data-scope="${escapeHtml(scope)}"
          data-post-id="${escapeHtml(postId)}"
          data-group-id="${escapeHtml(groupId)}"
          ${!postId || isOpening ? "disabled" : ""}
        >${isOpening ? "Opening…" : "Join conversation"}</button>
      </div>
      ${options.showFeedback === false ? "" : renderSimilarThoughtFeedback(postId)}
    </article>
  `;
}

// ── renderFullscreenLoading ──
// Theme-aware splash that matches the iOS launch screen: the dark wordmark
// (LOT_clear.svg) on light backgrounds, the light wordmark (LOT_dark_clear.svg)
// on dark. Uses <picture> + prefers-color-scheme so the correct asset is chosen
// by the engine before paint — no JS timing, no flash on Android WebView.
export function renderFullscreenLoading(): string {
  return `
    <section class="splash-loader" aria-label="Loading">
      <picture>
        <source srcset="/assets/LOT_dark_clear.svg" media="(prefers-color-scheme: dark)" />
        <img src="/assets/LOT_clear.svg" alt="LOT" class="splash-logo" />
      </picture>
    </section>
  `;
}

// ── renderExpandableText ──
export function renderExpandableText(text: string, key: string, className = ""): string {
  const cleanText = escapeHtml(text || "");
  const expanded = state.expandedPostIds.has(key);
  const needsClamp = (text || "").length > 180;
  const clampedClass = needsClamp && !expanded ? "clamped" : "";

  return `
    <div class="${className}">
      <div class="expandable ${clampedClass}" id="expand-${key}">${cleanText.replace(/\n/g, "<br />")}</div>
      ${needsClamp ? `<button class="read-more" data-action="toggle-expand" data-key="${key}">${expanded ? "Read less" : "Read more"}</button>` : ""}
    </div>
  `;
}

// ── renderPostCard ──
export function renderPostCard(post: SharedPost | CommunityPost, scope = "shared"): string {
  const author = renderAuthorMeta(post);
  const relative = toRelativeDate(post.sharedAt || post.createdAt);
  const responseLabel = "Respond";
  const isOwnPost = isOwnUid(post.authorUid);
  const resonanceCount = scope === "shared"
    ? Math.max(Number(state.resonanceCountOverrides[post.id] ?? (post as SharedPost).resonanceCount ?? 0), 0)
    : 0;
  const hasResonated = scope === "shared" && state.resonatedPostIds.has(post.id);
  const isSaved = scope === "shared" && state.savedPostIds.has(post.id);
  const resonanceLabel = "Felt it";
  const resonanceAction = scope === "shared" && !isOwnPost && state.user
    ? `<button class="btn-respond felt-btn ${hasResonated ? "felt-active" : ""}" data-action="tap-resonance" data-post-id="${escapeHtml(post.id)}" aria-pressed="${hasResonated ? "true" : "false"}"><span class="felt-heart">♥</span><span>${escapeHtml(resonanceLabel)}</span></button>`
    : "";
  const ownResonance = scope === "shared" && isOwnPost
    ? ownerStatsPill(post as SharedPost)
    : "";
  const saveAction = scope === "shared" && state.user
    ? `<button class="post-mod-btn" data-action="toggle-save-post" data-post-id="${escapeHtml(post.id)}">${isSaved ? "Remove saved thought" : "Save thought"}</button>`
    : "";
  const shareAction = `<button class="post-mod-btn" data-action="share-post" data-scope="${scope}" data-post-id="${escapeHtml(post.id)}">Share</button>`;

  // Keep safety and ownership tools available without making every thought
  // look like a moderation dashboard.
  const modActions = [
    saveAction,
    shareAction,
    (state.user && scope === "shared" && isOwnPost)
      ? `<button class="post-mod-btn danger" data-action="delete-post" data-post-id="${post.id}">Delete post</button>`
      : "",
    (state.user && scope === "shared" && !isOwnPost)
      ? `<button class="post-mod-btn" data-action="hide-post" data-post-id="${post.id}">Hide post</button>`
      : "",
    (state.user && scope === "shared" && !isOwnPost)
      ? `<button class="post-mod-btn" data-action="report-post" data-post-id="${post.id}">Report post</button>`
      : "",
    (state.user && scope === "shared" && !isOwnPost && post.attribution !== "anonymous")
      ? `<button class="post-mod-btn danger" data-action="block-post-author" data-post-id="${post.id}">Block author</button>`
      : "",
    (scope === "community" && isOwnPost) ? `<button class="post-mod-btn" data-action="delete-community-post" data-post-id="${post.id}">Delete</button>` : "",
  ].filter(Boolean).join("");

  // Presence, never counts — one quiet line that says the thought landed.
  const replyCount = Math.max(Number((post as SharedPost).replyCount ?? 0), 0);
  const replyPresence = scope === "shared" && replyCount > 0
    ? `<div class="presence-line">${replyCount === 1 ? "Someone continued this thought" : "People are continuing this thought"}</div>`
    : "";

  // iOS parity: the shelf this thought sits on, as the server decided.
  // Tapping it opens that shelf.
  const topicTag = scope === "shared" ? state.discoveryTagsByPostId[post.id] : undefined;
  const topicTagHtml = topicTag
    ? `<button class="post-topic-tag" data-action="set-discovery-direction" data-direction="${escapeHtml(topicTag.id)}" aria-label="Show thoughts in ${escapeHtml(topicTag.label)}">${escapeHtml(topicTag.label)}</button>`
    : "";

  // Thought first, person second: the text is the hero, the author is a
  // quiet byline directly under it, and actions come last.
  return `
    <article class="post-card" data-key="post:${escapeHtml(post.id)}">
      ${renderExpandableText(post.text, `post-${scope}-${post.id}`)}
      ${topicTagHtml}
      <div class="post-card-footer">
        <div class="meta">${author} • ${relative}</div>
        ${modActions ? `
          <details class="post-more">
            <summary aria-label="More post actions" title="More actions">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>
            </summary>
            <div class="post-more-menu">${modActions}</div>
          </details>
        ` : ""}
      </div>
      ${replyPresence}
      <div class="actions">
        <div class="actions-primary">
          <button class="btn-respond" data-action="open-thread" data-scope="${scope}" data-post-id="${post.id}" data-focus-reply="true">${escapeHtml(responseLabel)}</button>
          ${resonanceAction}
          ${ownResonance}
        </div>
      </div>
    </article>
  `;
}

function renderThreadPostCard(post: SharedPost | CommunityPost, scope = "shared"): string {
  const author = renderAuthorMeta(post);
  const relative = toRelativeDate(post.sharedAt || post.createdAt);
  const responseLabel = "Respond";
  const isOwnPost = isOwnUid(post.authorUid);
  const resonanceCount = scope === "shared"
    ? Math.max(Number(state.resonanceCountOverrides[post.id] ?? (post as SharedPost).resonanceCount ?? 0), 0)
    : 0;
  const hasResonated = scope === "shared" && state.resonatedPostIds.has(post.id);
  const resonanceAction = scope === "shared" && !isOwnPost && state.user
    ? `<button class="btn-respond felt-btn ${hasResonated ? "felt-active" : ""}" data-action="tap-resonance" data-post-id="${escapeHtml(post.id)}" aria-pressed="${hasResonated ? "true" : "false"}"><span class="felt-heart">♥</span><span>Felt it</span></button>`
    : "";
  const ownResonance = scope === "shared" && isOwnPost
    ? ownerStatsPill(post as SharedPost)
    : "";
  const shareAction = `<button class="thread-icon-action" data-action="share-post" data-scope="${scope}" data-post-id="${escapeHtml(post.id)}" aria-label="Share thought" title="Share">
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
  </button>`;
  const modActions = [
    scope === "shared" && state.user
      ? `<button class="post-mod-btn" data-action="toggle-save-post" data-post-id="${escapeHtml(post.id)}">${state.savedPostIds.has(post.id) ? "Remove saved thought" : "Save thought"}</button>`
      : "",
    (state.user && scope === "shared" && isOwnPost)
      ? `<button class="post-mod-btn danger" data-action="delete-post" data-post-id="${post.id}">Delete post</button>`
      : "",
    (state.user && scope === "shared" && !isOwnPost)
      ? `<button class="post-mod-btn" data-action="hide-post" data-post-id="${post.id}">Hide post</button>`
      : "",
    (state.user && scope === "shared" && !isOwnPost)
      ? `<button class="post-mod-btn" data-action="report-post" data-post-id="${post.id}">Report post</button>`
      : "",
    (state.user && scope === "shared" && !isOwnPost && post.attribution !== "anonymous")
      ? `<button class="post-mod-btn danger" data-action="block-post-author" data-post-id="${post.id}">Block author</button>`
      : "",
  ].filter(Boolean).join("");

  return `
    <article class="thread-post-card">
      <div class="thread-post-meta">${author} · ${relative}</div>
      <div class="thread-post-text">${escapeHtml(post.text || "").replace(/\n/g, "<br />")}</div>
      <div class="thread-post-actions">
        <div class="actions-primary">
          <button class="btn-respond" data-action="focus-reply">${escapeHtml(responseLabel)}</button>
          ${resonanceAction}
          ${ownResonance}
        </div>
        <div class="actions-end">
          ${shareAction}
          ${modActions ? `
            <details class="post-more">
              <summary aria-label="More post actions" title="More actions">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>
              </summary>
              <div class="post-more-menu">${modActions}</div>
            </details>
          ` : ""}
        </div>
      </div>
    </article>
  `;
}

// ── renderReplyRow ──
export function renderReplyRow(reply: Reply): string {
  const isOwn = isOwnUid(reply.authorUid);
  const nestedClass = Number(reply.depth || 1) > 1 ? "nested" : "";
  const anonymousColorIndex = Number(reply.anonymousColorIndex || 0);
  const anonymousMeta = `
    <span class="anonymous-id">
      <span class="anonymous-dot anonymous-dot-${anonymousColorIndex % 8}"></span>
      <span>Anonymous</span>
    </span>
  `;
  const author = escapeHtml(reply.authorUsername || shortUid(reply.authorUid));
  const authorLabel = isOwn ? `${author} <span class="you-tag">you</span>` : author;
  const authorMeta = reply.attribution === "username" && reply.authorUid
    ? `<button class="author-link" data-action="open-public-profile" data-uid="${escapeHtml(reply.authorUid)}">${authorLabel}</button>`
    : anonymousMeta;
  const authorAvatar = reply.attribution === "username" && reply.authorUid
    ? profileAvatarHTML(reply.authorAvatarURL, reply.authorUsername || shortUid(reply.authorUid), "reply-avatar")
    : "";

  return `
    <div class="reply ${nestedClass}" data-key="reply:${escapeHtml(reply.id)}">
      <div class="row spread">
        <div class="reply-meta">
          ${authorAvatar}
          <div class="meta"><strong>${authorMeta}</strong> • ${toRelativeDate(reply.createdAt)}</div>
        </div>
        <div class="row">
          ${!isOwn ? `<button class="btn-ghost btn-sm" data-action="report-reply" data-reply-id="${reply.id}">Report</button>` : ""}
          ${isOwn ? `<button class="btn-ghost btn-sm" data-action="delete-reply" data-reply-id="${reply.id}">Delete</button>` : ""}
        </div>
      </div>
      ${renderExpandableText(reply.text, `reply-${reply.id}`)}
      ${Number(reply.depth || 1) < 2 ? `<button class="btn-ghost btn-sm reply-btn" data-action="reply-to" data-reply-id="${reply.id}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;vertical-align:-1px"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>Reply</button>` : ""}
    </div>
  `;
}

type SimilarThoughtsCardOptions = {
  title?: string;
  thoughts?: SimilarThought[];
  loading?: boolean;
  embedded?: boolean;
  emptyText?: string;
  showFeedback?: boolean;
};

// ── renderSimilarThoughtsCard ──
export function renderSimilarThoughtsCard(insight: ThreadInsight | null, options: SimilarThoughtsCardOptions = {}): string {
  const sourceThoughts = options.thoughts ?? insight?.similarThoughts ?? [];
  const thoughts = Array.isArray(sourceThoughts) ? sourceThoughts.slice(0, 3) : [];
  const loading = options.loading ?? (state.threadInsightLoading || insight?.status === "pending");
  const title = options.title || "People are thinking something similar";
  const emptyText = options.emptyText || "";
  if (!loading && thoughts.length === 0 && !emptyText) return "";

  return `
    <section class="ai-card similar-thoughts-card ${options.embedded ? "embedded" : ""}">
      <div class="row spread">
        <h4>${escapeHtml(title)}</h4>
        ${loading ? `<span class="meta">Matching…</span>` : ""}
      </div>
      ${loading ? `
        <div class="ai-skeleton-stack">
          <div class="ai-skeleton-block"></div>
          <div class="ai-skeleton-block"></div>
          <div class="ai-skeleton-block"></div>
        </div>
      ` : thoughts.length > 0
        ? thoughts.map((thought) => renderSimilarThoughtItem(thought, { showFeedback: options.showFeedback })).join("")
        : `<div class="empty similar-empty">${escapeHtml(emptyText)}</div>`}
    </section>
  `;
}

// ── renderUserMatchesCard ──
export function renderUserMatchesCard(): string {
  if (!Array.isArray(state.userMatches) || state.userMatches.length === 0) return "";
  return `
    <section class="panel" style="margin-top:12px;">
      <div class="panel-header">
        <div>
          <h3>You may resonate with</h3>
          <div class="muted">People thinking along similar lines.</div>
        </div>
      </div>
      <div class="stack">
        ${state.userMatches.slice(0, 3).map((match) => {
          const label = (Array.isArray(match.topSharedTensions) && match.topSharedTensions[0])
              ? `Shared tension: ${match.topSharedTensions[0]}`
            : (Array.isArray(match.topSharedThemes) && match.topSharedThemes[0])
              ? `Shared theme: ${match.topSharedThemes[0]}`
              : "A similar reflection pattern";
          const handle = match.username ? `@${match.username}` : `@${shortUid(match.uid || "")}`;
          return `
            <article class="ai-user-card">
              <strong>${escapeHtml(handle)}</strong>
              ${match.displayName && match.displayName !== match.username ? `<div class="meta" style="margin-top:2px">${escapeHtml(match.displayName)}</div>` : ""}
              <div class="meta">${escapeHtml(label)}</div>
            </article>
          `;
        }).join("")}
      </div>
    </section>
  `;
}

// ── renderEchoForNote ──
export function renderEchoForNote(noteId: string): string {
  const echo = Array.isArray(state.echoItems)
    ? state.echoItems.find((item) => item.sourceEntryId === noteId || item.matchedEntryId === noteId)
    : null;
  if (!echo) return "";

  return `
    <section class="ai-card compact">
      <div class="row spread">
        <h4>Echo</h4>
        <span class="meta">${escapeHtml(echoHeadline(echo.daysApart, echo.sharedTension))}</span>
      </div>
      <div class="ai-copy">
        <div><strong>Echo</strong><span>${escapeHtml(echo.sharedTension || "You've thought about this before")}</span></div>
      </div>
    </section>
  `;
}

function thinkingThemePhrase(label: string): string {
  const key = label.toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
  const map: Record<string, string> = {
    direction: "figuring out where to go",
    difficulty: "when things get hard",
    choice: "decisions that keep returning",
    identity: "questions about who you are becoming",
    distance: "questions around distance",
    acceptance: "questions around acceptance",
    endurance: "questions around endurance",
    resilience: "pushing forward when it gets hard",
  };
  if (map[key]) return map[key];
  if (key.split(" ").length <= 2) {
    if (key.includes("love")) return "questions around love";
    if (key.includes("future")) return "figuring out what comes next";
    if (key.includes("self")) return "questions around yourself";
    return key ? `questions around ${key}` : "a returning thought";
  }
  return label.charAt(0).toLowerCase() + label.slice(1);
}

function thinkingProfileParagraph(profile: ThinkingProfileSnapshot | null): string {
  if (!profile || !Array.isArray(profile.lines)) return "";
  return profile.lines
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .map((line) => /[.!?]$/.test(line) ? line : `${line}.`)
    .join(" ");
}

function notePreview(entryId: string): string {
  const note = state.notes.find((item) => item.id === entryId);
  const text = String(note?.text || "").replace(/\s+/g, " ").trim();
  return text.length > 92 ? `${text.slice(0, 89)}...` : text;
}

function sortedPatternEntryIds(pattern: ThinkingPattern): string[] {
  return [...pattern.entryIds].sort((a, b) => {
    const left = state.notes.find((note) => note.id === a)?.createdAt?.getTime() || 0;
    const right = state.notes.find((note) => note.id === b)?.createdAt?.getTime() || 0;
    return left - right;
  });
}

function patternStatus(pattern: ThinkingPattern): string {
  return pattern.isEmerging ? "emerging" : "confirmed";
}

function activePatterns(): ThinkingPattern[] {
  return state.thinkingTimeline.patterns.filter((pattern) => {
    const lastSeen = timestampToDate(pattern.lastSeenAt);
    const days = Math.floor((Date.now() - lastSeen.getTime()) / 86400000);
    return days < 21;
  });
}

function renderThinkingPatternCard(pattern: ThinkingPattern): string {
  const ids = sortedPatternEntryIds(pattern);
  const firstPreview = notePreview(ids[0] || "");
  const lastPreview = notePreview(ids[ids.length - 1] || "");
  const samePreview = !firstPreview || firstPreview === lastPreview;
  const lastSeen = toRelativeDate(pattern.lastSeenAt);
  const status = patternStatus(pattern);
  return `
    <button class="thinking-pattern-card ${status}" data-action="open-thinking-pattern" data-pattern-id="${escapeHtml(pattern.id)}">
      <div class="row spread thinking-pattern-head">
        <h4>${escapeHtml(pattern.title)}</h4>
        <span class="thinking-status ${status}">${status}</span>
      </div>
      <div class="meta">${pattern.entryCount} thoughts · last seen ${escapeHtml(lastSeen)} ago</div>
      ${pattern.tensionArc?.copy ? `<div class="thinking-arc ${escapeHtml(pattern.tensionArc.state)}">${escapeHtml(pattern.tensionArc.copy)}</div>` : ""}
      ${samePreview ? `
        <div class="thinking-preview newest">${escapeHtml(firstPreview)}</div>
      ` : `
        <div class="thinking-preview oldest">${escapeHtml(firstPreview)}</div>
        <div class="thinking-preview-arrow">→</div>
        <div class="thinking-preview newest">${escapeHtml(lastPreview)}</div>
      `}
    </button>
  `;
}

function renderThinkingProfileCard(): string {
  const profile = state.thinkingTimeline.profile;
  const paragraph = thinkingProfileParagraph(profile);
  if (!profile || !paragraph || Number(profile.confidenceScore || 0) < 0.7) return "";
  return `
    <section class="thinking-card thinking-profile">
      <div class="thinking-card-title">How your thinking moves</div>
      <div class="meta">Observed across ${Number(profile.basedOnEntryCount || 0)} notes</div>
      <p>${escapeHtml(paragraph)}</p>
      <div class="meta">Last updated ${escapeHtml(toRelativeDate(profile.generatedAt))}</div>
    </section>
  `;
}

function renderThinkingThemesCard(): string {
  const themes = state.thinkingTimeline.themes.slice(0, 5);
  if (!themes.length) return "";
  return `
    <section class="thinking-card">
      <div class="thinking-card-title">What keeps coming up</div>
      <div class="thinking-theme-list">
        ${themes.map((theme, index) => `
          <div class="thinking-theme-row">
            <span>${index + 1}</span>
            <strong>${escapeHtml(thinkingThemePhrase(theme.label))}</strong>
            <em>${Number(theme.entryCount || 0)} notes</em>
          </div>
        `).join("")}
      </div>
    </section>
  `;
}

function renderThinkingTimelinePanel(): string {
  const timeline = state.thinkingTimeline;
  const patterns = activePatterns();
  const aiReady = Number(timeline.aiReadyEntryCount || 0);
  const loading = timeline.loading;
  const expanded = timeline.isExpanded === true;
  const enabled = state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted;
  const disabled = loading || !enabled;
  const topPattern = patterns[0];
  const themePreview = timeline.themes.slice(0, 2).map((theme) => thinkingThemePhrase(theme.label));
  const summaryTitle = topPattern
    ? `You keep coming back to ${topPattern.title.toLowerCase()}`
    : enabled
      ? "Private patterns from your notes"
      : "Private AI reflection is off";
  const summaryBody = topPattern?.tensionArc?.copy
    || (themePreview.length ? `Mostly around ${themePreview.join(" and ")}.` : "Expand when you want to check what keeps returning.");

  return `
    <section class="thinking-panel ${expanded ? "is-expanded" : "is-collapsed"}">
      <div class="thinking-panel-top">
        <button class="thinking-toggle" data-action="toggle-thinking-timeline" aria-expanded="${expanded ? "true" : "false"}">
          <span class="thinking-orb" aria-hidden="true">⌁</span>
          <span>
            <strong>Thinking over time</strong>
            <em>${enabled ? "Private pattern shelf" : "Enable note AI to unlock patterns"}</em>
          </span>
          <span class="thinking-chevron">${expanded ? "−" : "+"}</span>
        </button>
        <button class="btn-secondary btn-sm" data-action="refresh-thinking-timeline" ${disabled ? "disabled" : ""}>${loading ? "Updating..." : "Update"}</button>
      </div>

      <button class="thinking-summary" data-action="toggle-thinking-timeline" aria-expanded="${expanded ? "true" : "false"}">
        <span>
          <strong>${escapeHtml(summaryTitle)}</strong>
          <em>${escapeHtml(summaryBody)}</em>
        </span>
        <span class="thinking-summary-stats">
          ${topPattern ? `<b>${topPattern.entryCount}</b><small>thoughts</small>` : `<b>${aiReady}</b><small>ready</small>`}
        </span>
      </button>

      ${expanded ? `
        ${!enabled ? `
          <div class="thinking-empty">Enable Private AI reflection to see returning patterns.</div>
        ` : `
          ${renderThinkingProfileCard()}
          ${renderThinkingThemesCard()}

          ${patterns.length > 0 ? `
            <div class="thinking-section-title">Returning patterns</div>
            <div class="thinking-pattern-list">
              ${patterns.map(renderThinkingPatternCard).join("")}
            </div>
          ` : ""}
        `}
      ` : ""}
      ${timeline.error ? `<div class="meta" style="margin-top:10px;">${escapeHtml(timeline.error)}</div>` : ""}
    </section>
  `;
}

function renderPatternDoorway(): string {
  const pattern = activePatterns()[0];
  if (!pattern || Number(pattern.entryCount || 0) < 2) return "";
  const sortedIds = sortedPatternEntryIds(pattern);
  const latestEntryId = sortedIds[sortedIds.length - 1] || "";
  const summary = pattern.tensionArc?.copy
    || `${pattern.entryCount} thoughts around ${pattern.title.toLowerCase()} recently.`;

  return `
    <section class="pattern-doorway">
      <span class="pattern-doorway-orb" aria-hidden="true">⌁</span>
      <span class="pattern-doorway-copy">
        <strong>Something keeps returning</strong>
        <span>${escapeHtml(summary)}</span>
      </span>
      <button
        class="btn-secondary btn-sm"
        data-action="open-companion"
        data-note-id="${escapeHtml(latestEntryId)}"
      >Open with Companion</button>
    </section>
  `;
}

function renderThinkingPatternDetail(): string {
  const patternId = state.thinkingTimeline.selectedPatternId;
  if (!patternId) return "";
  const pattern = state.thinkingTimeline.patterns.find((item) => item.id === patternId);
  if (!pattern) return "";
  const ids = sortedPatternEntryIds(pattern);
  return `
    <div class="profile-overlay" role="dialog" aria-modal="true" aria-label="${escapeHtml(pattern.title)} pattern">
      <section class="profile-sheet thinking-detail">
        <div class="modal-header thinking-detail-header">
          <div class="thinking-detail-title">
            <h3>${escapeHtml(pattern.title)}</h3>
            <div class="meta">${pattern.entryCount} thoughts over ${Math.max(1, Number(pattern.daysActive || 1))} days</div>
          </div>
          <button class="btn-icon" data-action="close-thinking-pattern" aria-label="Close">×</button>
        </div>
        <div class="thinking-detail-body">
          ${pattern.tensionArc?.copy ? `<div class="thinking-detail-arc ${escapeHtml(pattern.tensionArc.state)}">${escapeHtml(pattern.tensionArc.copy)}</div>` : ""}
          ${pattern.themes.length ? `<div class="thinking-detail-themes">${pattern.themes.slice(0, 4).map((theme) => `<span>${escapeHtml(thinkingThemePhrase(theme))}</span>`).join("")}</div>` : ""}
          <div class="thinking-entry-list">
            ${ids.map((id, index) => {
              const note = state.notes.find((item) => item.id === id);
              if (!note) return "";
              const stage = index === 0 ? "Earlier" : index === ids.length - 1 ? "Latest" : "Then";
              return `
                <article class="thinking-entry">
                  <div class="row spread thinking-entry-meta">
                    <span>${escapeHtml(stage)}</span>
                    <span>${escapeHtml(toRelativeDate(note.createdAt))} ago</span>
                  </div>
                  <p>${escapeHtml(note.text)}</p>
                </article>
              `;
            }).join("")}
          </div>
          ${Number(pattern.publicConversationCount || 0) >= 2 && (pattern.publicMatches || []).length > 0 ? `
            <div class="thinking-public-bridge">
              <strong>Others are thinking about this too</strong>
              ${(pattern.publicMatches || []).slice(0, 2).map((match) => renderSimilarThoughtItem(match, { showFeedback: false })).join("")}
            </div>
          ` : ""}
        </div>
      </section>
    </div>
  `;
}

// ── renderThreadModal ──
export function renderThreadModal(): string {
  if (!state.threadModal) return "";

  const post = state.threadModal.post;
  const replies = sortedReplies(state.threadReplies);
  const targetReply = state.replyingToId
    ? state.threadReplies.find((r) => r.id === state.replyingToId) || null
    : null;
  const isAnonymousThread = post.attribution === "anonymous";
  const canReplyAnonymously = (state.threadModal.scope === "shared" || state.threadModal.scope === "community")
    && !!state.user
    && (isAnonymousThread || ("allowsAnonymousReplies" in post && post.allowsAnonymousReplies === true));
  const canWriteEncrypted = state.noteEncryption.unlocked;

  return `
    <div class="modal-backdrop">
      <div class="modal thread-modal" role="dialog" aria-modal="true" aria-labelledby="thread-title" data-scroll-preserve="thread-modal">
        <div class="modal-drag-handle" aria-hidden="true"></div>
        <div class="modal-header">
          <h3 id="thread-title">Thought</h3>
          <button class="btn-icon modal-close-btn" data-action="close-thread" aria-label="Close" title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        ${renderThreadPostCard(post, state.threadModal.scope)}

        <div class="modal-section">
          <div class="row spread">
            <h4>Responses</h4>
          </div>
          ${state.threadLoading ? `<div class="empty" style="margin-top:10px;">Loading responses...</div>` : ""}
          ${!state.threadLoading && replies.length === 0 ? `<div class="empty thread-empty"><strong>No responses yet</strong><br />Write what this brought up for you.</div>` : ""}
          ${!state.threadLoading ? replies.map((reply) => renderReplyRow(reply)).join("") : ""}
        </div>

        ${state.guestMode && !state.user ? `
          <div class="modal-section">
            <div class="guest-reply-cta">
              <p>Sign in to add your perspective to this conversation.</p>
              <button class="btn-primary" data-action="guest-sign-in">Sign In to Respond</button>
            </div>
          </div>
        ` : `
        <div class="modal-section">
          ${targetReply ? `<div class="meta" style="margin-bottom:8px;">Replying to ${escapeHtml(targetReply.attribution === "username" ? (targetReply.authorUsername || shortUid(targetReply.authorUid)) : "Anonymous")}</div>` : ""}
          ${canWriteEncrypted ? "" : `<div class="settings-note">Set up or unlock Note Encryption in Settings before responding.</div>`}
          <textarea placeholder="Write a response" data-field="reply-text" ${canWriteEncrypted ? "" : "disabled"}>${escapeHtml(state.replyText)}</textarea>
          <div class="row spread" style="margin-top:10px">
            <div class="row" style="gap:8px;align-items:center;flex-wrap:wrap">
              ${targetReply ? `<button class="btn-ghost btn-sm" data-action="cancel-reply-target">Cancel target</button>` : ""}
              ${canReplyAnonymously
                    ? `<select data-field="reply-attribution" class="cd-compose-attr">
                        <option value="anonymous" ${state.replyAttribution === "anonymous" ? "selected" : ""}>Anonymous</option>
                        <option value="username" ${state.replyAttribution === "username" ? "selected" : ""}>With Username</option>
                       </select>`
                    : `<div class="cd-compose-attr" aria-label="Reply attribution">With Username</div>`}
            </div>
            <button class="btn-primary" data-action="send-reply" ${isBusy("create-reply") || !canWriteEncrypted ? "disabled" : ""}>Respond</button>
          </div>
        </div>
        `}
      </div>
    </div>
  `;
}

// ── renderAuthView ──
export function renderAuthView(): string {
  const appleSvg = `<svg width="18" height="20" viewBox="0 0 814 1000" fill="currentColor"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.9 0 663 0 541.8c0-207.5 135.4-317.3 268.2-317.3 71 0 130.3 46.4 174.5 46.4 42.8 0 109.8-49 192.5-49 31 0 134.2 2.6 198.4 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/></svg>`;
  const googleSvg = `<svg width="18" height="18" viewBox="0 0 48 48"><path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"/><path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.1 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.7 7.4 6.3 14.7z"/><path fill="#FBBC05" d="M24 46c5.9 0 10.9-1.9 14.6-5.2l-6.7-5.5C29.9 36.8 27.1 38 24 38c-6.1 0-11.2-4.1-13.1-9.6l-7 5.4C7.5 41.1 15.2 46 24 46z"/><path fill="#EA4335" d="M44.5 20H24v8.5h11.8c-.9 2.9-2.7 5.4-5.1 7.1l6.7 5.5C41.3 37.6 45 31.6 45 24c0-1.3-.2-2.7-.5-4z"/></svg>`;
  const showIOSInstall = isIOSVisitor();

  return `
    <section class="auth-shell">
      <div class="auth-hero">
        ${showIOSInstall ? `
          <a class="auth-appstore-card" href="${APP_STORE_URL}" target="_blank" rel="noreferrer">
            <img src="/assets/app-store-icon.png" alt="" class="auth-appstore-icon" />
            <span class="auth-appstore-copy">
              <strong>LOT for iPhone</strong>
              <span>Open the full app experience.</span>
            </span>
            <span class="auth-appstore-pill">Get</span>
          </a>
        ` : ""}

        <div class="auth-brand">
          <img src="/assets/app-store-icon.png" alt="LOT" class="auth-app-icon" />
          <h1 class="auth-headline">LOT</h1>
          <p class="auth-tagline">A private place to write, understand yourself, and connect when you choose.</p>
          <p class="auth-plan-note">Start free. LOT Plus adds more Companion and deeper insights.</p>
        </div>

        <div class="auth-card">
          <div class="auth-card-section">
            <div class="stack" style="gap:10px">
              <button class="btn-social btn-apple" data-action="auth-apple">
                ${appleSvg}
                Continue with Apple
              </button>
              <button class="btn-social btn-google" data-action="auth-google">
                ${googleSvg}
                Continue with Google
              </button>
            </div>
          </div>

          <div class="auth-card-section">
            <div class="auth-divider">or continue with email</div>
            <form id="email-auth-form" class="stack" style="gap:12px; margin-top:14px">
              <div class="auth-input-group">
                <input name="email" type="email" required aria-label="Email" placeholder="Email" autocomplete="email" />
                <input name="password" type="password" required minlength="6" aria-label="Password" placeholder="Password" autocomplete="${state.authMode === "signin" ? "current-password" : "new-password"}" />
              </div>
              <button class="btn-primary" type="submit" data-action="auth-email-submit" style="width:100%; padding:13px;">
                ${state.authMode === "signin" ? "Sign In" : "Create Account"}
              </button>
            </form>
          </div>

          <div class="auth-card-section">
            <div class="auth-footer">
              <button class="btn-link" data-action="toggle-auth-mode">
                ${state.authMode === "signin" ? "Create an account" : "Already have an account?"}
              </button>
              <button class="btn-link" data-action="auth-reset-password">Forgot Password?</button>
            </div>
          </div>
        </div>

        <p class="auth-legal">
          By continuing, you agree to the
          <a href="https://superable.net/lot/termofuse" target="_blank" rel="noreferrer">Terms of Use</a>
          and
          <a href="https://superable.net/lot/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. LOT has no tolerance for objectionable content or abusive users.
        </p>

        <button class="auth-browse-btn" data-action="browse-as-guest">
          Browse the feed without signing in
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
    </section>
  `;
}

// ── renderUsernameOnboarding ──
export function renderUsernameOnboarding(): string {
  const email = escapeHtml(state.user?.email || "");
  return `
    <section class="auth-shell">
      <div class="auth-hero">
        <div class="auth-brand">
          <img src="/assets/app-store-icon.png" alt="LOT" class="auth-app-icon" />
          <h1 class="auth-headline">Almost there</h1>
          <p class="auth-tagline">Choose your username</p>
        </div>

        <div class="auth-card">
          <div class="auth-card-section">
            <p style="margin:0 0 14px; color:var(--text-2); font-size:0.95rem; line-height:1.5;">
              This is how your friends find you. You can always post anonymously.
            </p>
            ${email ? `<p style="margin:0 0 16px; font-size:0.84rem; color:var(--text-3);">${email}</p>` : ""}
            <form id="username-form" class="stack" style="gap:12px">
              <div class="auth-input-group">
                <input name="username" minlength="3" maxlength="20" required placeholder="your_username" autocomplete="username" autocapitalize="none" />
              </div>
              <button class="btn-primary" type="submit" style="width:100%; padding:13px;" ${isBusy("username") ? "disabled" : ""}>
                Continue
              </button>
            </form>
          </div>
          <div class="auth-card-section">
            <p class="auth-legal" style="margin:0;">
              By continuing you accept our
              <a href="https://superable.net/lot/termofuse" target="_blank" rel="noreferrer">Terms</a>
              and
              <a href="https://superable.net/lot/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>, including LOT's no-tolerance safety rules.
            </p>
          </div>
        </div>
      </div>
    </section>
  `;
}

// ── renderPassphraseOnboarding ──
// Required step right after username: every account sets an encryption
// passphrase so Notes and community posts are end-to-end encrypted from day
// one. Kept deliberately simple — one passphrase, one confirm, remembered on
// this device by default — and reuses the existing
// `setup-cross-platform-encryption` action + field names.
export function renderPassphraseOnboarding(): string {
  const busy = isBusy("setup-cross-platform-encryption");
  return `
    <section class="auth-shell">
      <div class="auth-hero">
        <div class="auth-brand">
          <div class="passphrase-shield" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2l8 3.5v5.5c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V5.5z"/>
              <path d="M9 12l2 2 4-4"/>
            </svg>
          </div>
          <h1 class="auth-headline">Secure your thoughts</h1>
          <p class="auth-tagline">Set an encryption passphrase</p>
        </div>

        <div class="auth-card">
          <div class="auth-card-section">
            <p style="margin:0 0 16px; color:var(--text-2); font-size:0.95rem; line-height:1.5;">
              Your notes and posts are encrypted with this passphrase. Only you can unlock them — LOT never sees it, so keep it somewhere safe.
            </p>
            <div class="stack" style="gap:14px;">
              <div class="auth-input-group">
                <label class="passphrase-label">Passphrase</label>
                <input type="password" name="cross-platform-passphrase" placeholder="At least 8 characters" autocomplete="new-password" autocapitalize="none" />
              </div>
              <div class="auth-input-group">
                <label class="passphrase-label">Confirm passphrase</label>
                <input type="password" name="cross-platform-confirm" placeholder="Repeat passphrase" autocomplete="new-password" autocapitalize="none" />
              </div>
              <label class="passphrase-remember">
                <span class="switch">
                  <input type="checkbox" name="cross-platform-remember" checked />
                  <span class="switch-ui"></span>
                </span>
                <span>Remember on this device so you don't have to re-enter it.</span>
              </label>
              <button class="btn-primary" data-action="setup-cross-platform-encryption" style="width:100%; padding:13px;" ${busy ? "disabled" : ""}>
                ${busy ? "Securing…" : "Secure my account"}
              </button>
            </div>
          </div>
          <div class="auth-card-section passphrase-skip-row">
            <button class="auth-browse-btn" data-action="skip-passphrase-setup">I'll set this up later</button>
          </div>
        </div>
      </div>
    </section>
  `;
}

// ── renderGuestGateView ──
function renderGuestGateView(tab: string): string {
  const labels: Record<string, string> = {
    write: "Write a thought",
    matches: "Matches",
    friends: "Friends",
    communities: "Communities",
    notes: "Thoughts",
    companion: "Companion",
    activity: "Activity",
    settings: "Settings",
    you: "You",
  };
  return `
    <section class="panel guest-gate-panel">
      <div class="guest-gate">
        <img src="/assets/app-store-icon.png" alt="LOT" class="guest-gate-icon" />
        <h2>${escapeHtml(labels[tab] || "This page")}</h2>
        <p>Sign in to access ${escapeHtml(labels[tab] || "this section")} and connect with others.</p>
        <div class="guest-gate-actions">
          <button class="btn-primary" data-action="guest-sign-in">Sign In</button>
          <button class="btn-secondary" data-action="set-tab" data-tab="feed">Browse Discovery</button>
        </div>
      </div>
    </section>
  `;
}

function nativeLockedNotesCard(): string {
  if (!state.user || state.noteEncryption.unlocked) return "";
  const copy = encryptionGateCopy();
  return `
    <section class="native-setup-card native-setup-card-notes">
      <div>
        <strong>${escapeHtml(copy.title)}</strong>
        <span>${escapeHtml(copy.message)}</span>
      </div>
      <button class="btn-primary btn-sm" data-action="set-tab" data-tab="settings">${escapeHtml(copy.button)}</button>
    </section>
  `;
}

function renderDailyAnchorCard(): string {
  const anchor = state.dailyAnchor;
  if (!anchor) return "";
  // Format selectedAt in the user's local timezone (e.g. "May 23").
  // Fall back to the raw date string only if the timestamp is missing.
  const dateLabel = anchor.selectedAt
    ? anchor.selectedAt.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : anchor.date;
  const date = dateLabel ? `<span>${escapeHtml(dateLabel)}</span>` : "";
  const attribution = anchor.attribution === "anonymous"
    ? "Anonymous"
    : (anchor.attribution || "LOT");

  return `
    <section class="daily-anchor-card">
      <div class="daily-anchor-top">
        <span class="daily-anchor-kicker">✦ Today's Anchor</span>
        ${date}
      </div>
      <blockquote>${escapeHtml(anchor.showOriginal ? anchor.originalText : anchor.text)}</blockquote>
      <div class="daily-anchor-footer">
        <span>${escapeHtml(attribution)}</span>
        ${anchor.isTranslated ? `
          <button class="daily-anchor-cta" data-action="toggle-anchor-original">
            ${anchor.showOriginal ? "View translation" : "View original"}
          </button>
        ` : ""}
        <button class="daily-anchor-cta" data-action="open-thread" data-scope="shared" data-post-id="${escapeHtml(anchor.postId)}">Continue thread →</button>
      </div>
    </section>
  `;
}

const discoveryStopWords = new Set([
  "the", "and", "but", "you", "your", "youre", "are", "was", "were", "this",
  "that", "with", "from", "have", "has", "had", "not", "for", "about", "what",
  "when", "where", "why", "how", "who", "would", "could", "should", "there",
  "their", "they", "them", "then", "than", "into", "like", "just", "really",
  "very", "more", "most", "much", "many", "some", "thing", "things", "people",
  "person", "thought", "thoughts", "everything", "anything", "something",
  "nothing", "actually", "because", "feel", "feels", "feeling", "felt", "been",
  "being", "still", "even", "only", "also", "dont", "cant", "wont", "didnt",
  "doesnt", "isnt", "lot", "app", "post", "public", "anonymous", "today",
  "don", "think", "thinking", "know", "never", "out", "all",
]);

function normalizeDiscoveryWord(word: string): string {
  const lower = word.toLowerCase();
  if (lower === "ai") return "AI";
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const discoveryPhraseByWord: Record<string, string> = {
  ai: "AI and work",
  career: "Work pressure",
  change: "Starting over",
  doubt: "Self-doubt",
  family: "Family expectations",
  friend: "Friendship",
  future: "Wanting certainty",
  identity: "Identity",
  lonely: "Loneliness",
  loneliness: "Loneliness",
  love: "Missing someone",
  meaning: "Meaning",
  money: "Money pressure",
  purpose: "Meaning",
  school: "School",
  success: "Feeling behind",
  uncertain: "Wanting certainty",
  uncertainty: "Wanting certainty",
  work: "Work pressure",
};

function localDiscoveryDirections(posts: SharedPost[]): DiscoveryDirection[] {
  const scores = new Map<string, number>();
  for (const post of posts) {
    const words = new Set(
      String(post.text || "")
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .split(/\s+/)
        .map((word) => word.trim().toLowerCase())
        .filter((word) => word.length >= 3 && word.length <= 16)
        .filter((word) => !discoveryStopWords.has(word))
        .slice(0, 80)
    );
    for (const word of words) {
      const signalBoost = /love|work|life|meaning|identity|success|family|friend|lonely|purpose|fear|future|money|school|career|ai/.test(word)
        ? 1.5
        : 1;
      scores.set(word, (scores.get(word) || 0) + signalBoost);
    }
  }

  // Only emit CURATED human directions. Raw frequency words like "Can",
  // "Day", "One", "Out" are never shown — a noisy single word reads as a
  // broken category, not a human direction. A word only becomes a chip if it
  // maps to a hand-written phrase in `discoveryPhraseByWord`.
  const dynamic = Array.from(scores.entries())
    .filter(([word, score]) => score >= 2 && discoveryPhraseByWord[word])
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([word]) => ({
      id: `local-${word}`,
      label: discoveryPhraseByWord[word],
      terms: [word],
      countText: null,
    }));

  const seen = new Set<string>();
  const deduped = dynamic.filter((direction) => {
    if (seen.has(direction.label)) return false;
    seen.add(direction.label);
    return true;
  }).slice(0, 7);
  // If we can't produce at least 2 quality directions, hide the row entirely
  // rather than show nonsense (the caller drops the block on empty).
  return deduped.length >= 2 ? deduped : [];
}

function postMatchesDiscoveryDirection(post: SharedPost, direction: DiscoveryDirection): boolean {
  const text = String(post.text || "").toLowerCase();
  const normalizedDirection = String(direction.label || "").toLowerCase();
  const mappedWords = Array.isArray(direction.terms) && direction.terms.length > 0
    ? direction.terms.map((term) => String(term || "").toLowerCase()).filter(Boolean)
    : Object.entries(discoveryPhraseByWord)
      .filter(([, phrase]) => phrase.toLowerCase() === normalizedDirection)
      .map(([word]) => word);
  const fallbackWords = `${normalizedDirection} ${String(direction.id || "").toLowerCase()}`
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter((word) => word.length >= 3);
  const terms = mappedWords.length > 0 ? mappedWords : fallbackWords;
  return terms.some((term) => text.includes(term));
}

function discoveryOrderedPosts(posts: SharedPost[]): SharedPost[] {
  if (state.discoveryRankedIds.length === 0) {
    return posts;
  }
  const rank = new Map(state.discoveryRankedIds.map((id, index) => [id, index]));
  const recentlyPublishedIds = new Set(state.recentlyPublishedPosts.map((post) => post.id));
  const freshOwn = posts
    .filter((post) => recentlyPublishedIds.has(post.id))
    .sort((a, b) => timestampToDate(b.sharedAt || b.createdAt).getTime() - timestampToDate(a.sharedAt || a.createdAt).getTime());
  const freshOwnIds = new Set(freshOwn.map((post) => post.id));
  const rankedKnown = posts
    .filter((post) => !freshOwnIds.has(post.id) && rank.has(post.id))
    .sort((a, b) => (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER));
  const unranked = posts
    .filter((post) => !freshOwnIds.has(post.id) && !rank.has(post.id))
    .sort((a, b) => timestampToDate(b.sharedAt || b.createdAt).getTime() - timestampToDate(a.sharedAt || a.createdAt).getTime());
  return [...freshOwn, ...rankedKnown, ...unranked];
}

// ── renderFeedView ──
export function renderFeedView(): string {
  const isGuest = state.guestMode && !state.user;
  const showNativeEncryptionNudge = !!state.user && !state.noteEncryption.unlocked;
  const encryptionCopy = encryptionGateCopy();

  const sections = state.feedSectionIds.all || {
    active: [],
    newThoughts: [],
    discovery: [],
  };
  const fallbackVisiblePosts = state.feedPosts.filter((post) => post.visibility === "public");
  const sectionPosts = [...sections.active, ...sections.newThoughts, ...sections.discovery];
  const seen = new Set<string>();
  // Own just-published thoughts lead until the materialized server feed
  // includes them; deduped by id so they never appear twice.
  const candidatePosts = discoveryOrderedPosts([...state.recentlyPublishedPosts, ...sectionPosts, ...fallbackVisiblePosts]
    .filter((post) => post.visibility === "public")
    .filter((post) => {
      if (seen.has(post.id)) return false;
      seen.add(post.id);
      return true;
    }));
  // Sticky order (iOS parity): the reader's visible list never reorders or
  // grows underneath them. The frozen display order only drops posts that
  // left the candidate set; new arrivals wait behind the "New thoughts" pill.
  // An explicit refresh clears feedDisplayOrder and adopts the fresh ranking.
  const byId = new Map(candidatePosts.map((post) => [post.id, post]));
  let displayIds = state.feedDisplayOrder.filter((id) => byId.has(id));
  if (displayIds.length === 0) {
    displayIds = candidatePosts.map((post) => post.id);
    state.feedPendingNewIds = [];
  } else {
    const knownIds = new Set(displayIds);
    const ownIds = new Set(state.recentlyPublishedPosts.map((post) => post.id));
    const incomingIds = candidatePosts.map((post) => post.id).filter((id) => !knownIds.has(id));
    // Own just-published thoughts appear instantly at the top and stay;
    // everything else waits behind the pill in candidate order.
    displayIds = [...incomingIds.filter((id) => ownIds.has(id)), ...displayIds];
    state.feedPendingNewIds = incomingIds.filter((id) => !ownIds.has(id));
  }
  state.feedDisplayOrder = displayIds;
  const allPosts = displayIds
    .map((id) => byId.get(id))
    .filter((post): post is SharedPost => !!post);
  const serverDirections = state.discoveryDirections
    .filter((direction) => direction.id.trim() && direction.label.trim())
    .slice(0, 8);
  const directions = serverDirections.length > 0 ? serverDirections : localDiscoveryDirections(allPosts);
  const selectedDirection = state.selectedDiscoveryDirection;
  const selectedDirectionObject = selectedDirection
    ? directions.find((direction) => direction.id === selectedDirection || direction.label === selectedDirection) || null
    : null;
  // Default on, like iOS: explicit or abusive text never reaches a reader
  // who didn't ask for it. (The web has no server-side moderation writer.)
  const safePosts = allPosts.filter((post) => !containsObjectionableContent(post.text));
  // Shelf membership is the server's decision when it has answered; local
  // matching only bridges the moment before it does.
  const shelf = state.discoveryShelfPostIds;
  const filteredPosts = selectedDirection
    ? safePosts.filter((post) => shelf
      ? shelf.has(post.id)
      : (selectedDirectionObject ? postMatchesDiscoveryDirection(post, selectedDirectionObject) : true))
    : safePosts;
  const visiblePosts = filteredPosts.slice(0, state.feedVisibleLimit);
  const hasMorePosts = visiblePosts.length < filteredPosts.length;

  const guestBanner = isGuest ? `
    <div class="guest-banner">
      <div class="guest-banner-text">
        <span>You're viewing shared thoughts. Sign in for your own private writing and Companion.</span>
      </div>
      <button class="btn-primary btn-sm" data-action="guest-sign-in">Sign in</button>
    </div>
  ` : "";

  return `
    <section class="panel discovery-view">
      ${guestBanner}
      <div class="panel-header feed-panel-header discovery-title-row">
        <div>
          <h2>What people are thinking</h2>
          <div class="muted">Thoughts people chose to share. Your private writing stays private.</div>
        </div>
        <div class="row">
          <button class="icon-btn" data-action="refresh-feed" aria-label="Refresh Discovery" title="Refresh">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15.5 6.2L3 16"/><path d="M3 21v-5h5"/></svg>
          </button>
        </div>
      </div>

      ${showNativeEncryptionNudge ? `
        <div class="native-setup-card">
          <div>
            <strong>${escapeHtml(encryptionCopy.title)}</strong>
            <span>${escapeHtml(encryptionCopy.message)}</span>
          </div>
          <button class="btn-primary btn-sm" data-action="set-tab" data-tab="settings">${escapeHtml(encryptionCopy.button)}</button>
        </div>
      ` : ""}

      ${!isGuest ? `
        <div class="discovery-write-entry">
          <div>
            <strong>Publish to Discovery</strong>
            <span>Share a public thought and let it find similar thoughts.</span>
          </div>
          <button class="btn-primary btn-sm" data-action="set-tab" data-tab="write" data-public-compose="true">Publish thought</button>
        </div>
      ` : ""}

      ${directions.length >= 2 ? `
        <div class="discovery-directions-block">
          <button class="direction-scroll direction-scroll-left" data-action="scroll-directions" data-dir="-1" aria-label="Scroll directions back" tabindex="-1">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <div class="discovery-directions" aria-label="What people are thinking about">
            <button class="direction-chip ${!selectedDirection ? "active" : ""}" data-action="set-discovery-direction" data-direction="">All</button>
            ${directions.map((direction) => `
              <button class="direction-chip ${selectedDirection === direction.id ? "active" : ""}" data-action="set-discovery-direction" data-direction="${escapeHtml(direction.id)}">${escapeHtml(direction.label)}</button>
            `).join("")}
          </div>
          <button class="direction-scroll direction-scroll-right" data-action="scroll-directions" data-dir="1" aria-label="Scroll directions forward" tabindex="-1">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>
      ` : ""}

      ${allPosts.length === 0 && !state.feedInitialLoadDone ? `
        <div class="discovery-skeleton" aria-busy="true" aria-label="Loading thoughts">
          ${[0, 1, 2].map(() => `
            <div class="post-skeleton">
              <div class="ai-skeleton-line w72"></div>
              <div class="ai-skeleton-line"></div>
              <div class="ai-skeleton-line w54"></div>
              <div class="post-skeleton-meta"><span class="ai-skeleton-line"></span></div>
            </div>`).join("")}
        </div>` : ""}
      ${allPosts.length === 0 && state.feedInitialLoadDone ? `<div class="empty empty-with-action"><strong>Discovery is quiet right now.</strong><span>When people share a thought, it shows up here.</span>${isGuest ? "" : `<button class="btn-secondary btn-sm" data-action="refresh-feed">Check again</button>`}</div>` : ""}

      ${allPosts.length > 0 ? `
        <div class="discovery-list-header">
          <h3>Interesting thoughts</h3>
        </div>
        ${visiblePosts.length === 0 ? `<div class="empty"><strong>Nothing here yet.</strong><span>Try another direction.</span></div>` : ""}
        ${state.feedPendingNewIds.length > 0 && !selectedDirection ? `
          <button class="new-thoughts-pill" data-action="reveal-new-thoughts">New thoughts</button>
        ` : ""}
        <div class="discovery-post-list">
          ${visiblePosts.map((post) => state.feedRevealAnimateIds.has(post.id)
            ? `<div class="post-reveal">${renderPostCard(post, "shared")}</div>`
            : renderPostCard(post, "shared")).join("")}
        </div>
        ${hasMorePosts ? `
          <div class="feed-progress">
            <div class="feed-load-sentinel" data-feed-load-more aria-hidden="true"></div>
            <button class="btn-secondary feed-load-more" data-action="load-more-feed">Show more thoughts</button>
          </div>
        ` : ""}
      ` : ""}
    </section>
  `;
}

// ── renderWriteView ──
function renderComposerResonance(): string {
  const resonance = state.composerResonance;
  if (!resonance) return "";
  const thoughts = resonance.thoughts.slice(0, 3);
  const firstSessionNoMatch = resonance.isFirstThought && thoughts.length === 0 && !resonance.isLoadingMore;
  const noMatch = thoughts.length === 0 && !resonance.isLoadingMore;
  const reflection = thoughts.length > 0
    ? matchReasonText(thoughts[0]?.matchReason)
    : "Your thought has a place to connect.";

  return `
    <section class="instant-resonance-card">
      <div class="row spread">
        <div>
          <div class="meta">Instant Resonance</div>
          <h3>${thoughts.length > 0 ? "People are thinking something similar" : "Your thought is in the network"}</h3>
        </div>
        <button class="btn-icon" data-action="dismiss-resonance" aria-label="Dismiss" title="Dismiss">x</button>
      </div>
      <div class="resonance-source">${escapeHtml(resonance.sourceText)}</div>
      <div class="resonance-reflection">${escapeHtml(reflection)}</div>
      ${resonance.isLoadingMore ? `<div class="empty">Finding nearby thoughts...</div>` : ""}
      ${firstSessionNoMatch ? `
        <div class="empty resonance-network-state">
          <strong>Your thought is in the network. We'll find what it connects to.</strong>
          <span>Try one more thought while the first one starts looking for its edges.</span>
        </div>
      ` : ""}
      ${!firstSessionNoMatch && noMatch ? `
        <div class="empty resonance-network-state">
          <strong>Your thought is in the network. We'll find what it connects to.</strong>
          <span>Continue it, or come back when a stronger match appears.</span>
        </div>
      ` : ""}
      ${thoughts.length > 0 ? `
        <div class="similar-thought-list">
          ${thoughts.map((thought) => renderSimilarThoughtItem(thought, { showFeedback: false })).join("")}
        </div>
      ` : ""}
      <div class="actions resonance-actions">
        <button class="btn-primary btn-sm" data-action="${thoughts.length > 0 ? "open-similar-thought" : "continue-resonance"}" data-scope="${escapeHtml(thoughts[0]?.scope || "sharedPost")}" data-post-id="${escapeHtml(thoughts[0]?.postId || "")}" data-group-id="${escapeHtml(thoughts[0]?.groupId || "")}">${thoughts.length > 0 ? "Respond" : "Continue this thought"}</button>
        ${thoughts.length > 0 ? `<button class="btn-secondary btn-sm" data-action="continue-resonance">Continue this thought</button>` : ""}
        <button class="btn-secondary btn-sm" data-action="share-resonance-match" ${thoughts.length === 0 ? "disabled" : ""}>Share this match</button>
      </div>
    </section>
  `;
}

export function renderWriteView(): string {
  const userNameAvailable = Boolean(state.profile?.username);
  const canWriteEncrypted = state.noteEncryption.unlocked;
  const encryptionCopy = encryptionGateCopy();
  const isPrivateNote = state.composer.visibility === "private";
  const isPublicThought = state.composer.visibility === "public";
  const safeReview = state.composer.safeReview;

  // Safe review step (iOS parity): before a public thought goes out, the
  // writer sees the safe version; the private original stays behind a toggle.
  if (safeReview) {
    return `
      <section class="panel write-panel safe-review-panel">
        <div class="panel-header">
          <div>
            <h2>Review before sharing</h2>
            <div class="muted">You wrote the real version. This is the one the world sees.</div>
          </div>
        </div>
        <div class="stack">
          <textarea class="safe-review-text" data-field="safe-review-text" aria-label="The version the world sees">${escapeHtml(safeReview.safeText)}</textarea>
          ${safeReview.changeCount > 0 ? `
            <div class="meta">Some personal details were removed automatically. Read it once before sharing.</div>
          ` : ""}
          <details class="safe-review-original">
            <summary>Show private original</summary>
            <div class="safe-review-original-text">${escapeHtml(safeReview.originalText).replace(/\n/g, "<br />")}</div>
          </details>
          <div class="row spread composer-actions write-actions">
            <button class="btn-secondary" data-action="cancel-share-review">Keep editing</button>
            <button class="btn-primary" data-action="confirm-share-post" ${isBusy("create-post") || !safeReview.safeText.trim() ? "disabled" : ""}>Share</button>
          </div>
        </div>
      </section>
    `;
  }

  const visibilityButton = (value: "private" | "public" | "friends", label: string, helper: string) => `
    <button
      type="button"
      class="write-choice ${state.composer.visibility === value ? "is-active" : ""}"
      data-field="composer-visibility"
      data-value="${value}"
      aria-pressed="${state.composer.visibility === value ? "true" : "false"}"
      ${canWriteEncrypted ? "" : "disabled"}
    >
      <strong>${label}</strong>
      <span>${helper}</span>
    </button>
  `;
  const attributionButton = (value: "anonymous" | "username", label: string, helper: string, disabled = false) => `
    <button
      type="button"
      class="write-choice ${state.composer.attribution === value ? "is-active" : ""}"
      data-field="composer-attribution"
      data-value="${value}"
      aria-pressed="${state.composer.attribution === value ? "true" : "false"}"
      ${canWriteEncrypted && !disabled ? "" : "disabled"}
    >
      <strong>${label}</strong>
      <span>${helper}</span>
    </button>
  `;
  return `
    <section class="panel write-panel">
      <div class="panel-header">
        <div>
          <h2>Write</h2>
        </div>
      </div>

      <div class="stack">
        ${canWriteEncrypted ? "" : `
          <div class="settings-note settings-note-setup">
            <span>${escapeHtml(encryptionCopy.message)}</span>
            <button class="btn-secondary btn-sm" data-action="set-tab" data-tab="settings">${escapeHtml(encryptionCopy.button)}</button>
          </div>
        `}
        <div class="write-editor-shell">
          <textarea data-field="composer-text" placeholder="Start writing. It can be messy." aria-label="Write your thought" ${canWriteEncrypted ? "" : "disabled"}>${escapeHtml(state.composer.text)}</textarea>
          <div class="write-editor-meta">
            <span>${state.composer.isListening ? "Listening…" : isPrivateNote ? "Private note — saved to Notes" : isPublicThought ? "Public — visible to everyone" : "Shared with friends only"}</span>
          </div>
        </div>

        <div class="write-controls ${isPrivateNote ? "is-private" : ""}">
          <div class="write-control-block">
              <div class="write-control-heading">
                <label>Visibility</label>
                <span>${isPrivateNote ? "Only you can read this." : isPublicThought ? "Anyone can read this." : "Only accepted friends can read this."}</span>
              </div>
              <div class="write-choice-group write-choice-group-visibility" role="group" aria-label="Visibility">
                ${visibilityButton("private", "Private", "Only you. Encrypted.")}
                ${visibilityButton("friends", "Friends", "Only mutual friends. Name optional.")}
                ${visibilityButton("public", "Public", "Anyone on LOT. You review a safe version first.")}
              </div>
            </div>

          ${isPrivateNote ? "" : `
            <div class="write-control-block">
              <div class="write-control-heading">
                <label>Attribution</label>
                <span>Your name stays private unless you sign it.</span>
              </div>
              <div class="write-choice-group write-choice-group-attribution" role="group" aria-label="Attribution">
                ${attributionButton("anonymous", "Anonymous", "No name shown")}
                ${attributionButton("username", "Username", userNameAvailable ? "Signed post" : "Set username first", !userNameAvailable)}
              </div>
            </div>
          `}
        </div>

        ${state.composer.visibility === "friends" ? `
          <label class="match-consent-row">
            <input type="checkbox" data-field="composer-match-consent" ${state.composer.matchConsent ? "checked" : ""} />
            <span>
              <strong>Allow matching to find similar thoughts</strong>
              <small>Your thought stays private. You approve the anonymous excerpt used for matching.</small>
            </span>
          </label>
        ` : isPublicThought ? `
          <div class="public-review-card">
            <div>
              <strong>Review before sharing</strong>
              <span>You wrote the real version. Before it goes out, you review the one the world sees.</span>
            </div>
          </div>
        ` : ""}

        <div class="row spread composer-actions write-actions">
          <span class="meta write-shortcut-hint">${/Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl"} + Enter to ${isPrivateNote ? "save" : "share"}</span>
          <div class="row">
            <button class="composer-mic ${state.composer.isListening ? "active" : ""}" data-action="toggle-composer-voice" aria-label="${state.composer.isListening ? "Stop voice input" : "Start voice input"}" title="${state.composer.isListening ? "Stop voice input" : "Voice input"}">${state.composer.isListening
              ? `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6.5" y="6.5" width="11" height="11" rx="2.5"/></svg>`
              : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`}</button>
            <button class="btn-primary" data-action="create-post" ${isBusy("create-post") || !canWriteEncrypted ? "disabled" : ""}>${isPrivateNote ? "Save note" : isPublicThought ? "Share" : "Share with friends"}</button>
          </div>
        </div>
      </div>
    </section>
    ${renderComposerResonance()}
  `;
}

// ── renderFriendsView ──
export function renderFriendsView(): string {
  const incoming = state.friends.filter((friend) => friend.status === "pending_incoming");
  const outgoing = state.friends.filter((friend) => friend.status === "pending_outgoing");
  const accepted = state.friends.filter((friend) => friend.status === "accepted");
  const hasSearchResults = state.friendSearchResults.length > 0;
  const requestsCount = incoming.length + outgoing.length;

  const tab = state.friendsTab;

  const renderFriendRow = (
    person: FriendRowPerson,
    subtitle: string,
    actionHtml = "",
    tone: "default" | "pending" | "suggested" = "default",
  ) => {
    const avatarLabel = person.displayName || person.username || person.uid;
    const bio = String(person.bio || "").trim();
    const subtitleText = bio || subtitle;
    return `
    <article class="friend-row friend-row-${tone}">
      <button class="friend-row-profile" data-action="open-public-profile" data-uid="${escapeHtml(person.uid)}">
        ${profileAvatarHTML(person.avatarURL, avatarLabel, "friend-row-avatar")}
        <div class="friend-row-body">
          ${renderPersonName(person)}
          <div class="friend-row-subtitle">${escapeHtml(subtitleText)}</div>
        </div>
        <span class="friend-row-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </span>
      </button>
      ${actionHtml ? `<div class="friend-row-actions">${actionHtml}</div>` : ""}
    </article>
  `;
  };

  const segment = (key: FriendsTab, label: string, count: number, accent = false) => `
    <button class="friends-seg-btn ${tab === key ? "active" : ""}" data-action="set-friends-tab" data-friends-tab="${key}">
      <span class="friends-seg-label">${label}</span>
      ${count > 0 ? `<span class="friends-seg-count ${accent ? "accent" : ""}">${count}</span>` : ""}
    </button>
  `;

  // ── Friends pane ──
  const friendsPane = accepted.length === 0
    ? `<div class="empty friends-empty-cta">
         <div class="friends-empty-emoji">👋</div>
         <h3>No friends yet</h3>
         <p class="muted">Discover people on LOT and send your first request.</p>
         <button class="btn-primary" data-action="set-friends-tab" data-friends-tab="discover">Discover people</button>
       </div>`
    : `<div class="friends-list friends-list-flush">${accepted.map((friend) => renderFriendRow(
        {
          uid: friend.friendUid,
          username: friend.username || "",
          displayName: friend.profile?.displayName,
          bio: friend.profile?.bio,
          avatarURL: friend.profile?.avatarURL,
        },
        "Connected",
        `<button class="btn-ghost btn-sm" data-action="remove-friend" data-uid="${friend.friendUid}" ${isBusy(`friend-remove-${friend.friendUid}`) ? "disabled" : ""}>Remove</button>`,
      )).join("")}</div>`;

  // ── Requests pane (incoming + sent) ──
  const requestsPane = requestsCount === 0
    ? `<div class="empty friends-empty-cta">
         <div class="friends-empty-emoji">📭</div>
         <h3>No pending requests</h3>
         <p class="muted">Invites you send or receive will show up here.</p>
       </div>`
    : `
      ${incoming.length > 0 ? `
        <div class="friends-pane-label">Waiting on you</div>
        <div class="friends-list friends-list-flush">${incoming.map((friend) => renderFriendRow(
          {
            uid: friend.friendUid,
            username: friend.username || "",
            displayName: friend.profile?.displayName,
            bio: friend.profile?.bio,
            avatarURL: friend.profile?.avatarURL,
          },
          "Wants to connect",
          `<button class="btn-primary btn-sm" data-action="accept-friend" data-uid="${friend.friendUid}" ${isBusy(`friend-accept-${friend.friendUid}`) ? "disabled" : ""}>Accept</button><button class="btn-ghost btn-sm" data-action="decline-friend" data-uid="${friend.friendUid}" ${isBusy(`friend-decline-${friend.friendUid}`) ? "disabled" : ""}>Decline</button>`,
          "pending",
        )).join("")}</div>
      ` : ""}
      ${outgoing.length > 0 ? `
        <div class="friends-pane-label">Sent by you</div>
        <div class="friends-list friends-list-flush friends-section-muted">${outgoing.map((friend) => renderFriendRow(
          {
            uid: friend.friendUid,
            username: friend.username || "",
            displayName: friend.profile?.displayName,
            bio: friend.profile?.bio,
            avatarURL: friend.profile?.avatarURL,
          },
          "Pending",
          `<span class="friends-status-chip">Pending</span>`,
          "pending",
        )).join("")}</div>
      ` : ""}
    `;

  // ── Discover pane (search + suggestions) ──
  const discoverPane = `
    <div class="friends-search-shell">
      <div class="friends-search-row">
        <input class="friends-search-input" placeholder="Search by username" value="${escapeHtml(state.friendSearch)}" data-field="friend-search" />
        <button class="btn-secondary friends-search-button" data-action="run-friend-search">Search</button>
      </div>
    </div>
    ${hasSearchResults ? `
      <div class="friends-pane-label">Search results</div>
      <div class="friends-list friends-list-flush">${state.friendSearchResults.map((user) => renderFriendRow(
        {
          uid: user.uid,
          username: user.username,
          displayName: user.profile?.displayName,
          bio: user.profile?.bio,
          avatarURL: user.profile?.avatarURL,
        },
        "LOT user",
        `<button class="btn-primary btn-sm" data-action="send-friend-request" data-uid="${user.uid}" ${isBusy(`friend-send-${user.uid}`) ? "disabled" : ""}>Add</button>`,
        "suggested",
      )).join("")}</div>
    ` : ""}
    <div class="friends-pane-label">Suggested for you</div>
    ${state.friendSuggestions.length === 0
      ? `<div class="empty friends-empty">No suggestions right now.</div>`
      : `<div class="friends-list friends-list-flush">${state.friendSuggestions.map((suggestion) => renderFriendRow(
          {
            uid: suggestion.uid,
            username: suggestion.username,
            displayName: suggestion.displayName,
            bio: suggestion.bio,
            avatarURL: suggestion.avatarURL,
          },
          "Recently active on LOT",
          `<button class="btn-primary btn-sm" data-action="send-friend-request" data-uid="${suggestion.uid}" ${isBusy(`friend-send-${suggestion.uid}`) ? "disabled" : ""}>Add</button>`,
          "suggested",
        )).join("")}</div>`}
  `;

  const activePane = tab === "discover" ? discoverPane : tab === "requests" ? requestsPane : friendsPane;

  return `
    <section class="friends-root friends-root-v2">
      <header class="friends-topbar">
        <h2>Friends</h2>
        <div class="friends-seg" role="tablist">
          ${segment("friends", "Friends", accepted.length)}
          ${segment("requests", "Requests", requestsCount, incoming.length > 0)}
          ${segment("discover", "Discover", 0)}
        </div>
      </header>
      <div class="friends-pane">
        ${activePane}
      </div>
    </section>
  `;
}

// ── Anonymous thought matches ───────────────────────────────────────────────
export function renderMatchesView(): string {
  const proposals = state.thoughtMatches
    .filter((match) => match.state === "proposalFound" || match.state === "waitingForOtherPerson")
    .sort((left, right) => right.updatedAt.getTime() - left.updatedAt.getTime());
  const searching = state.thoughtMatches.filter((match) => match.state === "searching");
  const conversations = state.thoughtMatchConversations;

  const excerpt = (title: string, text: string) => `
    <button class="match-excerpt" data-action="open-match-excerpt" data-title="${escapeHtml(title)}" data-text="${escapeHtml(text)}">
      <span class="match-excerpt-label">${escapeHtml(title)}</span>
      <span class="match-excerpt-copy">${escapeHtml(text)}</span>
      ${text.length > 180 ? `<span class="match-read-full">Read full thought</span>` : ""}
    </button>
  `;

  return `
    <section class="matches-root">
      <header class="matches-header">
        <span class="matches-orb" aria-hidden="true">◎</span>
        <div>
          <h2>${state.portraitMatching.enabled ? "Portrait matches" : "Matches"}</h2>
          <p>${state.portraitMatching.enabled ? "Meet someone whose portrait echoes yours in a few meaningful ways." : "Discover when someone else is holding a thought like yours."}</p>
        </div>
      </header>
      ${renderPortraitMatchingSection()}

      ${state.thoughtMatchError ? `<div class="settings-note">${escapeHtml(state.thoughtMatchError)}</div>` : ""}
      ${!state.thoughtMatchConfig.thoughtMatchingEnabled && !state.thoughtMatchLoading ? `
        <div class="match-empty">
          <span class="match-empty-icon">✦</span>
          <h3>Matches are opening soon</h3>
          <p>LOT is checking match quality before opening this to everyone.</p>
        </div>
      ` : ""}

      ${conversations.length ? `
        <section class="match-section">
          <h3>Conversations</h3>
          <div class="match-stack">
            ${conversations.map((conversation) => `
              <button class="match-conversation-row" data-action="open-match-conversation" data-conversation-id="${escapeHtml(conversation.id)}">
                <span class="match-conversation-icon">↔</span>
                <span class="match-conversation-copy">
                  <strong>${escapeHtml(conversation.lastMessagePreview || conversation.otherExcerpt)}</strong>
                  <small>${escapeHtml(conversation.explanation || "An anonymous thought connected with yours.")}</small>
                </span>
                ${conversation.unreadCount > 0 ? `<span class="match-unread">${conversation.unreadCount}</span>` : `<span class="match-chevron">›</span>`}
              </button>
            `).join("")}
          </div>
        </section>
      ` : ""}

      ${proposals.length ? `
        <section class="match-section">
          <h3>Possible matches</h3>
          <div class="match-stack">
            ${proposals.map((match) => `
              <article class="match-proposal">
                ${excerpt("Your thought", match.excerpt)}
                ${excerpt("Another thought", match.otherExcerpt || "")}
                ${match.explanation ? `<p class="match-explanation">${escapeHtml(match.explanation)}</p>` : ""}
                ${match.state === "waitingForOtherPerson" ? `
                  <div class="match-waiting">Waiting for the other person</div>
                ` : match.proposalId ? `
                  <div class="match-proposal-actions">
                    <button class="btn-primary" data-action="respond-match" data-proposal-id="${escapeHtml(match.proposalId)}" data-accept="true" ${isBusy(`match-proposal-${match.proposalId}`) ? "disabled" : ""}>I’d talk about this</button>
                    <button class="btn-secondary" data-action="respond-match" data-proposal-id="${escapeHtml(match.proposalId)}" data-accept="false" ${isBusy(`match-proposal-${match.proposalId}`) ? "disabled" : ""}>Not this one</button>
                  </div>
                ` : ""}
              </article>
            `).join("")}
          </div>
        </section>
      ` : ""}

      ${searching.length ? `
        <section class="match-section">
          <h3>Still looking</h3>
          <div class="match-stack">
            ${searching.map((match) => `
              <button class="match-searching" data-action="open-match-excerpt" data-title="Your thought" data-text="${escapeHtml(match.excerpt)}">
                <span>${escapeHtml(match.excerpt)}</span>
                <small>⌁ We’ll keep looking.</small>
              </button>
            `).join("")}
          </div>
        </section>
      ` : ""}

      ${state.thoughtMatchLoading ? `<div class="match-loading">Looking for your matches…</div>` : ""}
      ${state.thoughtMatchConfig.thoughtMatchingEnabled && !state.thoughtMatchLoading && !conversations.length && !proposals.length && !searching.length ? `
        <div class="match-empty">
          <span class="match-empty-icon">◎</span>
          <h3>You are not alone starts with a real overlap.</h3>
          <p>We’ll only show strong matches. Public thoughts can find similar thoughts; private thoughts ask you before using an anonymous excerpt.</p>
          <button class="btn-primary" data-action="set-tab" data-tab="write">Write a thought</button>
        </div>
      ` : ""}
    </section>
  `;
}

// ── Community palette — deterministic accent per group id ──
const COMMUNITY_PALETTES = [
  { bg: "#0F172A", text: "#fff" },
  { bg: "#1D4ED8", text: "#fff" },
  { bg: "#475569", text: "#fff" },
  { bg: "#0F766E", text: "#fff" },
  { bg: "#4338CA", text: "#fff" },
  { bg: "#155E75", text: "#fff" },
];
function communityPalette(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return COMMUNITY_PALETTES[h % COMMUNITY_PALETTES.length];
}

// ── renderCommunityBrowse ── (the home/browse page) ──────────────────────
function renderCommunityBrowse(): string {
  const inviteCount = state.communitiesInvites.length;
  return `
    <div class="cb-root">
      <!-- Top action bar -->
      <div class="cb-topbar">
        <div>
          <h2 class="cb-title">Communities</h2>
          <p class="cb-subtitle">Groups where thoughts become conversations.</p>
        </div>
        <div class="cb-topbar-actions">
          <button class="cb-pill-btn" data-action="community-overlay" data-overlay="join">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
            Join with code
          </button>
          <button class="cb-pill-btn primary" data-action="community-overlay" data-overlay="create">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New community
          </button>
        </div>
      </div>

      ${inviteCount > 0 ? `
      <!-- Invites banner -->
      <div class="cb-invites-banner">
        <div class="cb-invites-banner-left">
          <span class="cb-invite-badge">${inviteCount}</span>
          <div>
            <div class="cb-invites-title">Pending invite${inviteCount > 1 ? "s" : ""}</div>
            <div class="cb-invites-sub">You've been invited to join a community.</div>
          </div>
        </div>
        <div class="cb-invites-list">
          ${state.communitiesInvites.map((g) => {
            const pal = communityPalette(g.id);
            return `
              <div class="cb-invite-item">
                <div class="cb-invite-cover" style="background:${pal.bg};">${escapeHtml((g.name || "?").slice(0,1))}</div>
                <div class="cb-invite-name">${escapeHtml(g.name)}</div>
                <button class="btn-primary btn-sm" data-action="accept-invite" data-community-id="${g.id}">Accept</button>
                <button class="btn-ghost btn-sm" data-action="deny-invite" data-community-id="${g.id}">Decline</button>
              </div>`;
          }).join("")}
        </div>
      </div>
      ` : ""}

      <!-- My communities -->
      ${state.communitiesMy.length > 0 ? `
      <div class="cb-section">
        <div class="cb-section-header">
          <span class="cb-section-label">My communities</span>
          <span class="cb-section-count">${state.communitiesMy.length}</span>
        </div>
        <div class="cb-grid">
          ${state.communitiesMy.map((g) => {
            const pal = communityPalette(g.id);
            const isSelected = state.selectedCommunityId === g.id;
            return `
              <button class="cb-community-tile ${isSelected ? "active" : ""}" data-action="open-community" data-community-id="${g.id}">
                <div class="cb-tile-cover" style="background:${pal.bg};">
                  <span class="cb-tile-letter" style="color:${pal.text};">${escapeHtml((g.name || "?").slice(0,1))}</span>
                </div>
                <div class="cb-tile-body">
                  <div class="cb-tile-name">${escapeHtml(g.name)}</div>
                  <div class="cb-tile-meta">${Number(g.memberCount || 0)} members · ${g.visibility === "private" ? "Private" : "Public"}</div>
                </div>
                <svg class="cb-tile-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>`;
          }).join("")}
        </div>
      </div>
      ` : `
      <div class="cb-empty-state">
        <div class="cb-empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
        </div>
        <strong>No communities yet</strong>
        <span>Create your own or join one with an invite code.</span>
      </div>
      `}

      <!-- Discover public -->
      ${state.communitiesPublic.length > 0 ? `
      <div class="cb-section">
        <div class="cb-section-header">
          <span class="cb-section-label">Discover</span>
          <span class="cb-section-count">${state.communitiesPublic.length} public</span>
        </div>
        <div class="cb-discover-list">
          ${state.communitiesPublic.map((g) => {
            const pal = communityPalette(g.id);
            const alreadyMember = state.communitiesMy.some((m) => m.id === g.id);
            const primaryAction = alreadyMember ? "open-community" : "join-community";
            const primaryLabel = alreadyMember ? "Open" : "Join";
            const helperLabel = alreadyMember ? "Open community" : "Join in one tap";
            return `
              <button class="cb-discover-item cb-discover-button" data-action="${primaryAction}" data-community-id="${g.id}">
                <div class="cb-discover-cover" style="background:${pal.bg};">${escapeHtml((g.name || "?").slice(0,1))}</div>
                <div class="cb-discover-body">
                  <div class="cb-discover-name">${escapeHtml(g.name)}</div>
                  <div class="cb-discover-purpose">${escapeHtml(g.purpose || "")}</div>
                  <div class="cb-discover-meta">${Number(g.memberCount || 0)} members · ${helperLabel}</div>
                </div>
                <span class="cb-discover-cta">${primaryLabel}</span>
              </button>`;
          }).join("")}
        </div>
      </div>
      ` : ""}

      <!-- Overlays rendered inline -->
      ${state.communityOverlay ? renderCommunityOverlay() : ""}
    </div>
  `;
}

// ── renderCommunityOverlay ── ─────────────────────────────────────────────
function renderCommunityOverlay(): string {
  if (state.communityOverlay === "create") {
    return `
      <div class="co-backdrop">
        <div class="co-sheet" role="dialog" aria-modal="true">
          <div class="co-sheet-handle"></div>
          <div class="co-sheet-header">
            <h3>New community</h3>
            <button class="btn-icon co-close" data-action="close-community-overlay" aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
            </button>
          </div>
          <form id="community-create-form" class="co-body">
            <div class="co-field">
              <label>Name</label>
              <input name="name" required maxlength="30" placeholder="e.g. Profit Pub" autofocus />
            </div>
            <div class="co-field">
              <label>Purpose</label>
              <textarea name="purpose" required maxlength="120" placeholder="What is this community for?" style="min-height:96px;"></textarea>
              <div class="co-field-hint">Max 120 characters</div>
            </div>
            <div class="co-visibility-picker">
              <label class="co-visibility-option">
                <input type="radio" name="visibility" value="public" checked />
                <div class="co-visibility-card">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                  <div>
                    <div class="co-vis-title">Public</div>
                    <div class="co-vis-desc">Anyone can discover and join</div>
                  </div>
                </div>
              </label>
              <label class="co-visibility-option">
                <input type="radio" name="visibility" value="private" />
                <div class="co-visibility-card">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                  <div>
                    <div class="co-vis-title">Private</div>
                    <div class="co-vis-desc">Invite only, not discoverable</div>
                  </div>
                </div>
              </label>
            </div>
            <button class="btn-primary co-submit" type="submit" ${isBusy("create-community") ? "disabled" : ""}>
              ${isBusy("create-community") ? "Creating…" : "Create community"}
            </button>
          </form>
        </div>
      </div>
    `;
  }
  if (state.communityOverlay === "join") {
    return `
      <div class="co-backdrop">
        <div class="co-sheet" role="dialog" aria-modal="true">
          <div class="co-sheet-handle"></div>
          <div class="co-sheet-header">
            <h3>Join with code</h3>
            <button class="btn-icon co-close" data-action="close-community-overlay" aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/></svg>
            </button>
          </div>
          <div class="co-body">
            <p class="co-join-hint">Ask the community owner for an invite code, then paste it below.</p>
            <div class="co-field">
              <label>Invite code</label>
              <input class="co-code-input" placeholder="e.g. LOT2026" value="${escapeHtml(state.joinCodeInput)}" data-field="join-code-input" autofocus spellcheck="false" />
            </div>
            <button class="btn-primary co-submit" data-action="join-by-code" ${isBusy("join-by-code") ? "disabled" : ""}>
              ${isBusy("join-by-code") ? "Joining…" : "Join community"}
            </button>
          </div>
        </div>
      </div>
    `;
  }
  return "";
}

// ── renderCommunityCard (kept for backward compat; not used in new layout) ──
export function renderCommunityCard(group: typeof state.communitiesMy[0], mode: "my" | "invite" | "discover"): string {
  const pal = communityPalette(group.id);
  const isSelected = state.selectedCommunityId === group.id;
  return `
    <button class="cb-community-tile ${isSelected ? "active" : ""}" data-action="open-community" data-community-id="${group.id}">
      <div class="cb-tile-cover" style="background:${pal.bg};">
        <span class="cb-tile-letter" style="color:${pal.text};">${escapeHtml((group.name || "?").slice(0,1))}</span>
      </div>
      <div class="cb-tile-body">
        <div class="cb-tile-name">${escapeHtml(group.name || "Untitled")}</div>
        <div class="cb-tile-meta">${Number(group.memberCount || 0)} members</div>
      </div>
    </button>
  `;
}

// ── renderCommunityDetail ── ──────────────────────────────────────────────
export function renderCommunityDetail(): string {
  if (!state.selectedCommunity) return renderCommunityBrowse();
  const group = state.selectedCommunity;
  const isOwner = state.selectedCommunityRole === "owner";
  const isAdmin = state.selectedCommunityRole === "owner" || state.selectedCommunityRole === "mod";
  const posts = [...state.selectedCommunityPosts];
  const pal = communityPalette(group.id);
  const tab = state.communityDetailTab;
  const encryptionCopy = encryptionGateCopy();

  return `
    <div class="cd-root">
      <!-- Back nav + header -->
      <div class="cd-header">
        <button class="cd-back" data-action="community-nav" data-page="browse">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Communities
        </button>
        <div class="cd-header-body">
          <div class="cd-cover" style="background:${pal.bg};">
            <span style="color:${pal.text}; font-size:1.6rem; font-weight:700;">${escapeHtml((group.name || "?").slice(0,1))}</span>
          </div>
          <div class="cd-header-text">
            <h2 class="cd-community-name">${escapeHtml(group.name)}</h2>
            <p class="cd-community-purpose">${escapeHtml(group.purpose)}</p>
          </div>
        </div>
        <div class="cd-header-chips">
          <span class="cd-chip ${group.visibility === "private" ? "private" : "public"}">${group.visibility === "private" ? "Private" : "Public"}</span>
          <span class="cd-chip neutral">${Number(group.memberCount || 0)} members</span>
          ${isAdmin ? `<button class="cd-chip settings-btn" data-action="community-nav" data-page="settings">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            Settings
          </button>` : ""}
          <button class="cd-chip reload-btn" data-action="reload-community">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/></svg>
            Refresh
          </button>
        </div>
        <!-- Tab bar -->
        <div class="cd-tabs">
          <button class="cd-tab ${tab === "posts" ? "active" : ""}" data-action="community-detail-tab" data-tab="posts">Posts <span class="cd-tab-count">${posts.length}</span></button>
          <button class="cd-tab ${tab === "members" ? "active" : ""}" data-action="community-detail-tab" data-tab="members">Members <span class="cd-tab-count">${state.selectedCommunityMembers.length}</span></button>
        </div>
      </div>

      <!-- TAB: Posts -->
      ${tab === "posts" ? `
      <div class="cd-posts-pane">
        <div class="cd-compose">
          ${state.noteEncryption.unlocked ? "" : `
            <div class="settings-note settings-note-action">
              <span>${escapeHtml(encryptionCopy.message)}</span>
              <button class="btn-secondary btn-sm" data-action="set-tab" data-tab="settings">${escapeHtml(encryptionCopy.button)}</button>
            </div>
          `}
          <textarea class="cd-compose-textarea" data-field="community-post-text" placeholder="Share something with ${escapeHtml(group.name)}…" ${state.noteEncryption.unlocked ? "" : "disabled"}>${escapeHtml(state.communityPostDraft.text)}</textarea>
          <div class="cd-compose-row">
            <select data-field="community-post-attribution" class="cd-compose-attr" ${state.noteEncryption.unlocked ? "" : "disabled"}>
              <option value="anonymous" ${state.communityPostDraft.attribution === "anonymous" ? "selected" : ""}>Post anonymously</option>
              <option value="username" ${state.communityPostDraft.attribution === "username" ? "selected" : ""}>Post as @${escapeHtml(state.profile?.username || "you")}</option>
            </select>
            <button class="btn-primary btn-sm cd-compose-publish" data-action="create-community-post" ${isBusy("create-community-post") || !state.noteEncryption.unlocked ? "disabled" : ""}>Publish</button>
          </div>
        </div>
        <div class="cd-feed">
          <div class="cd-feed-toolbar">
            <div class="meta">Conversation order</div>
            <div class="tabs cd-feed-sort">
              <button data-action="set-community-sort" data-sort="active" class="${state.communitySort === "active" ? "active" : ""}">Active</button>
              <button data-action="set-community-sort" data-sort="newest" class="${state.communitySort === "newest" ? "active" : ""}">Newest</button>
            </div>
          </div>
          ${posts.length === 0
            ? `<div class="empty" style="padding:48px 0; margin:0;">This community is waiting for its first real thought. Start with what keeps returning.</div>`
            : posts.map((p) => renderPostCard(p, "community")).join("")
          }
        </div>
      </div>
      ` : ""}

      <!-- TAB: Members -->
      ${tab === "members" ? `
      <div class="cd-members-pane">
        ${isAdmin ? `
        <div class="cd-invite-bar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;color:var(--text-3)"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
          <input placeholder="Invite by username…" value="${escapeHtml(state.communityInviteSearch)}" data-field="community-invite-username" class="cd-invite-input" />
          <button class="btn-secondary btn-sm" data-action="invite-member" ${isBusy("invite-user") ? "disabled" : ""}>Invite</button>
        </div>
        ` : ""}
        <div class="cd-members-grid">
          ${state.selectedCommunityMembers.map((member) => {
            const name = usernameCache.get(member.uid) || shortUid(member.uid);
            const pal2 = communityPalette(member.uid);
            const canRemove = isAdmin && member.role !== "owner" && member.uid !== state.user?.uid;
            return `
              <div class="cd-member-card">
                <div class="cd-member-avatar" style="background:${pal2.bg}; color:${pal2.text};">${name.slice(0,1).toUpperCase()}</div>
                <div class="cd-member-main">
                  <div class="cd-member-name">@${escapeHtml(name)}</div>
                  <div class="cd-member-role-row">
                    ${member.role === "owner" ? `<span class="cd-member-role owner">Owner</span>` : ""}
                    ${member.role === "mod" ? `<span class="cd-member-role mod">Mod</span>` : ""}
                    ${member.role === "member" ? `<span class="cd-member-role neutral">Member</span>` : ""}
                  </div>
                </div>
                ${canRemove ? `<button class="btn-danger btn-sm cd-member-remove" data-action="remove-community-member" data-member-uid="${escapeHtml(member.uid)}" ${isBusy(`remove-community-member-${member.uid}`) ? "disabled" : ""}>Remove</button>` : ""}
              </div>`;
          }).join("")}
        </div>
      </div>
      ` : ""}
    </div>
  `;
}

// ── renderCommunitySettings ── ────────────────────────────────────────────
function renderCommunitySettings(): string {
  const group = state.selectedCommunity!;
  const isOwner = state.selectedCommunityRole === "owner";

  return `
    <div class="cs-root">
      <div class="cs-header">
        <button class="cd-back" data-action="community-nav" data-page="detail">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back to ${escapeHtml(group.name)}
        </button>
        <h2 class="cs-title">Settings</h2>
      </div>

      <div class="cs-body">
        ${isOwner ? `
        <div class="cs-section">
          <div class="cs-section-title">Join code</div>
          <form id="join-code-settings-form" class="cs-form">
            <label class="cs-toggle-row">
              <div>
                <div class="cs-toggle-label">Enable join code</div>
                <div class="cs-toggle-desc">Let anyone with the code join without a manual invite.</div>
              </div>
              <input type="checkbox" name="enabled" ${group.codeJoinEnabled ? "checked" : ""} style="width:auto;" />
            </label>
            <div class="grid-2">
              <div>
                <label>Code</label>
                <input name="code" value="${escapeHtml(group.joinCode || "")}" placeholder="e.g. LOT2026" maxlength="16" />
              </div>
              <div>
                <label>Expires (optional)</label>
                <input type="datetime-local" name="expiresAt" value="${group.joinCodeExpiresAt ? toLocalDatetimeInput(group.joinCodeExpiresAt) : ""}" />
              </div>
            </div>
            <button class="btn-primary btn-sm cs-save" type="submit" data-action="save-join-code" ${isBusy("update-join-code") ? "disabled" : ""}>Save changes</button>
          </form>
        </div>
        ` : ""}

        <div class="cs-section">
          <div class="cs-section-title">Membership</div>
          <div class="cs-danger-row">
            <div>
              <div class="cs-toggle-label">${isOwner ? "Owner account" : "Leave community"}</div>
              <div class="cs-toggle-desc">${isOwner ? "Owners can delete the community from the danger zone below." : "You will lose access to all posts and members."}</div>
            </div>
            ${isOwner ? "" : `<button class="btn-ghost btn-sm" style="color:var(--red);" data-action="leave-community" data-community-id="${group.id}">Leave</button>`}
          </div>
        </div>

        ${isOwner ? `
        <div class="cs-section">
          <div class="cs-section-title">Danger zone</div>
          <div class="cs-danger-row">
            <div>
              <div class="cs-toggle-label">Delete community</div>
              <div class="cs-toggle-desc">Permanently removes this community, its posts, invites, join code, and member list.</div>
            </div>
            <button class="btn-danger btn-sm" data-action="delete-community" data-community-id="${group.id}" ${isBusy(`delete-community-${group.id}`) ? "disabled" : ""}>Delete</button>
          </div>
        </div>
        ` : ""}
      </div>
    </div>
  `;
}

// ── renderCommunitiesView ── ──────────────────────────────────────────────
export function renderCommunitiesView(): string {
  const sub = state.communitySubPage;
  if (sub === "detail" && state.selectedCommunity) return renderCommunityDetail();
  if (sub === "settings" && state.selectedCommunity) return renderCommunitySettings();
  return renderCommunityBrowse();
}

// ── renderNotesView ──
export function renderNotesView(): string {
  const canWriteEncryptedNotes = state.noteEncryption.unlocked;
  const encryptionCopy = encryptionGateCopy();
  const isEditingNote = Boolean(state.noteDraft.id);
  return `
    <section class="panel notes-panel">
      <div class="panel-header">
        <div>
          <h2>Thoughts</h2>
        </div>
      </div>

      <button class="thoughts-write-card" data-action="set-tab" data-tab="write" data-private-compose="true">
        <span class="thoughts-write-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
        </span>
        <span class="thoughts-write-hint">What are you thinking?</span>
        <span class="thoughts-write-chevron" aria-hidden="true">&rsaquo;</span>
      </button>

      ${renderCompanionDashboardCard()}

      ${nativeLockedNotesCard()}

      ${isEditingNote ? `
      <div class="notes-composer">
        <div class="notes-composer-heading">
          <strong>Edit private note</strong>
          <span>Only you can read this</span>
        </div>
        ${!canWriteEncryptedNotes ? `
          <div class="settings-note settings-note-action" style="margin-bottom:10px;">
            <span>${escapeHtml(encryptionCopy.message)}</span>
            <button class="btn-secondary btn-sm" data-action="set-tab" data-tab="settings">${escapeHtml(encryptionCopy.button)}</button>
          </div>
        ` : ""}
        <textarea data-field="note-text" placeholder="Start wherever you are…" aria-label="Private note" ${canWriteEncryptedNotes ? "" : "disabled"}>${escapeHtml(state.noteDraft.text)}</textarea>
        <div class="note-privacy-line">
          <span>Encrypted note. It does not need to be polished.</span>
        </div>
        <div class="row spread composer-actions" style="margin-top:10px;">
          <div class="row">
            <span class="meta">Changes stay private</span>
          </div>
          <div class="row composer-actions-buttons">
            <button class="btn-secondary btn-sm" data-action="cancel-note-edit">Cancel</button>
            <button class="btn-primary" data-action="save-note" ${isBusy("save-note") || !canWriteEncryptedNotes ? "disabled" : ""}>Save changes</button>
          </div>
        </div>
      </div>
      ` : ""}

      ${state.notes.length === 0 ? `
        <section class="thoughts-empty" aria-label="No thoughts yet">
          <div class="thoughts-empty-lede">
            <span class="thoughts-empty-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
            </span>
            <div>
              <h3>Your thoughts live here</h3>
              <p>Write whatever is on your mind. It stays private unless you choose to share it.</p>
            </div>
          </div>
          <button class="thoughts-empty-companion" data-action="open-standalone-companion">
            <span class="companion-talk-avatar" aria-hidden="true"><img src="/assets/Companion_LOT.svg" alt="" /></span>
            <span class="thoughts-empty-companion-copy">
              <strong>Not sure where to start?</strong>
              <span>Talk it through with Companion first.</span>
            </span>
            <span class="thoughts-write-chevron" aria-hidden="true">&rsaquo;</span>
          </button>
        </section>` : `<div class="notes-list">${state.notes
        .map((note) => {
          const displaySimilarThoughts = noteFallbackSimilarThoughts(note.id, note.text);
          const similarKey = inlineSimilarNoteKey(note.id);
          const similarExpanded = state.expandedSimilarThoughtIds.has(similarKey);
          const similarLoading = state.inlineSimilarThoughtLoading.has(similarKey) || state.noteInsights[note.id]?.status === "pending";
          const canSeeSimilar = displaySimilarThoughts.length > 0 || (state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted && !note.isEncrypted);
          return `
          <article class="note-card">
            <div class="row spread">
              <div class="meta">Updated ${toRelativeDate(note.updatedAt)}</div>
              ${note.isEncrypted ? `<span class="badge">Encrypted</span>` : ""}
            </div>
            ${renderExpandableText(note.text, `note-${note.id}`)}
            <div class="note-card-actions">
              <button class="btn-secondary btn-sm" data-action="open-companion" data-note-id="${escapeHtml(note.id)}">Open with Companion</button>
              ${canSeeSimilar ? `
                <button
                  class="btn-secondary btn-sm"
                  data-action="toggle-similar-thoughts"
                  data-similar-source="note"
                  data-note-id="${escapeHtml(note.id)}"
                  aria-expanded="${similarExpanded ? "true" : "false"}"
                >${similarExpanded ? "Hide similar public thoughts" : "See similar public thoughts"}</button>
              ` : ""}
              <details class="post-more note-more">
                <summary aria-label="More note actions" title="More actions">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7"/><circle cx="12" cy="12" r="1.7"/><circle cx="19" cy="12" r="1.7"/></svg>
                </summary>
                <div class="post-more-menu">
                  <button class="post-mod-btn" data-action="edit-note" data-note-id="${escapeHtml(note.id)}">Edit note</button>
                  <button class="post-mod-btn danger" data-action="delete-note" data-note-id="${note.id}" ${isBusy(`delete-note-${note.id}`) ? "disabled" : ""}>Delete note</button>
                </div>
              </details>
            </div>
            ${similarExpanded ? renderSimilarThoughtsCard(state.noteInsights[note.id] || null, {
              title: "Similar public thoughts",
              thoughts: displaySimilarThoughts,
              embedded: true,
              loading: similarLoading,
              emptyText: "No strong similar public thoughts yet.",
            }) : ""}
          </article>
        `;
        })
        .join("")}</div>`}
      ${renderThinkingPatternDetail()}
    </section>
  `;
}

// ── Companion (moment-first) ────────────────────────────────────────────────
// iOS parity: Companion opens with ONE observation and one action — not a
// dashboard. Patterns come from users/{uid}/thinking/v1; server-written
// `displayTitle`/`observation` are preferred when present.

const JUNK_PATTERN_TITLES = new Set(["", "none", "no inner conflict", "unclear", "unknown", "n/a", "nothing"]);

export const COMPANION_ACK_STORAGE_KEY = "companionAcknowledgedPatternIds";

export function readAcknowledgedPatternIds(): string[] {
  try {
    const raw = window.localStorage.getItem(COMPANION_ACK_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function sentenceCase(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function patternDisplayTitle(pattern: ThinkingPattern): string {
  const candidates = [pattern.displayTitle, pattern.title, pattern.tension, pattern.themes[0]];
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value && !JUNK_PATTERN_TITLES.has(value.toLowerCase())) return sentenceCase(value);
  }
  return "";
}

function patternArcLine(pattern: ThinkingPattern): string {
  switch (pattern.tensionArc?.state) {
    case "intensifying": return "It's been feeling sharper lately.";
    case "softening":    return "It seems quieter than it was.";
    case "cycling":      return "It keeps changing shape.";
    default:              return "It keeps coming back.";
  }
}

function companionMomentPatterns(): ThinkingPattern[] {
  return activePatterns().filter((pattern) => patternDisplayTitle(pattern) !== "");
}

/// The conversation entry — iOS talkBar parity: a quiet capsule with the
/// Companion mark, an invitation, and a send arrow. Both page states show it.
function companionTalkBar(hint = "What's on your mind?"): string {
  return `
    <button class="companion-talk-bar" data-action="open-standalone-companion" aria-label="Talk to Companion. ${escapeHtml(hint)}">
      <span class="companion-talk-avatar" aria-hidden="true"><img src="/assets/Companion_LOT.svg" alt="" /></span>
      <span class="companion-talk-hint">${escapeHtml(hint)}</span>
      <span class="companion-talk-send" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
      </span>
    </button>
  `;
}

type CompanionMomentPage = {
  eyebrow: string;
  ledeHtml: string;
  arc: string;
  questionHtml?: string;
  continueAction: string;
  continueNoteId: string;
  notNowPatternId?: string;
};

export function renderCompanionView(): string {
  const patterns = companionMomentPatterns();
  const acknowledgedList = readAcknowledgedPatternIds();
  const acknowledged = new Set(acknowledgedList);
  const unacknowledged = patterns.filter((pattern) => !acknowledged.has(pattern.id));
  const stillOpenQuestion = state.companionContext.memories
    .map((memory) => String(memory.nextQuestion || "").trim())
    .find(Boolean) || "";

  // The lead moment: the first unacknowledged pattern. If everything has been
  // acknowledged, an open question from memory takes the moment; failing that,
  // quietly cycle back to the least-recently acknowledged pattern (the ack
  // list preserves acknowledgement order).
  let lead: ThinkingPattern | null = null;
  if (unacknowledged.length > 0) {
    lead = unacknowledged[0];
  } else if (!stillOpenQuestion && patterns.length > 0) {
    lead = [...patterns].sort(
      (left, right) => acknowledgedList.indexOf(left.id) - acknowledgedList.indexOf(right.id),
    )[0];
  }

  // iOS momentPager parity: the lead plus up to two more threads, every card
  // wearing the same chrome, dots underneath when there is more than one.
  const pages: CompanionMomentPage[] = [];
  const pagePattern = (pattern: ThinkingPattern, eyebrow: string, notNow: boolean): CompanionMomentPage => {
    const title = patternDisplayTitle(pattern);
    const observation = String(pattern.observation || "").trim();
    const sortedIds = sortedPatternEntryIds(pattern);
    const latestEntryId = sortedIds[sortedIds.length - 1] || "";
    return {
      eyebrow,
      ledeHtml: observation
        ? escapeHtml(observation)
        : `You keep coming back to <strong>${escapeHtml(title)}</strong>.`,
      arc: patternArcLine(pattern),
      continueAction: latestEntryId ? "open-companion" : "open-standalone-companion",
      continueNoteId: latestEntryId,
      notNowPatternId: notNow ? pattern.id : undefined,
    };
  };
  if (lead) {
    pages.push(pagePattern(lead, "Where we left off", true));
  } else if (stillOpenQuestion) {
    pages.push({
      eyebrow: "Still open",
      ledeHtml: "Last time, you left a question with me.",
      arc: "No need to answer it well. Just honestly.",
      questionHtml: `&ldquo;${escapeHtml(stillOpenQuestion)}&rdquo;`,
      continueAction: "open-standalone-companion",
      continueNoteId: "",
    });
  }
  const leadId = lead ? lead.id : null;
  for (const pattern of patterns) {
    if (pages.length >= 3) break;
    if (pattern.id === leadId) continue;
    pages.push(pagePattern(pattern, "Where we left off", false));
  }

  const activePage = Math.min(Math.max(state.companionMomentPage, 0), Math.max(pages.length - 1, 0));
  const page = pages[activePage];
  const deckHtml = page ? `
    <section class="companion-deck${pages.length > 1 ? " companion-deck-stacked" : ""}">
      <div class="companion-moment">
        <div class="companion-eyebrow">${escapeHtml(page.eyebrow)}</div>
        <p class="companion-moment-lede">${page.ledeHtml}</p>
        ${page.questionHtml ? `<p class="companion-moment-question">${page.questionHtml}</p>` : ""}
        <p class="companion-moment-arc">${escapeHtml(page.arc)}</p>
        <div class="companion-moment-actions">
          <button
            class="btn-primary companion-continue"
            data-action="${page.continueAction}"
            ${page.continueNoteId ? `data-note-id="${escapeHtml(page.continueNoteId)}"` : ""}
          >Continue</button>
          ${page.notNowPatternId ? `<button class="companion-not-now" data-action="companion-not-now" data-pattern-id="${escapeHtml(page.notNowPatternId)}">Not now</button>` : ""}
        </div>
      </div>
      ${pages.length > 1 ? `
        <div class="companion-deck-dots" role="tablist" aria-label="More moments">
          ${pages.map((_, index) => `
            <button
              class="companion-deck-dot${index === activePage ? " active" : ""}"
              data-action="companion-moment-page"
              data-page="${index}"
              role="tab"
              aria-selected="${index === activePage ? "true" : "false"}"
              aria-label="Moment ${index + 1} of ${pages.length}"
            ></button>
          `).join("")}
        </div>
      ` : ""}
    </section>
  ` : "";

  // Chat-first empty state — iOS parity: the input is the protagonist, the
  // memory model is stated honestly, and ONE optional door sits below.
  const dayOfYear = Math.floor((Date.now() - Date.UTC(new Date().getUTCFullYear(), 0, 0)) / 86400000);
  const starterQuestion = DAILY_PROMPT_FALLBACK[dayOfYear % DAILY_PROMPT_FALLBACK.length];
  const emptyHtml = !page ? `
    <section class="companion-empty-lede">
      <h3>Say what&rsquo;s on your mind.</h3>
      <p>Nothing here is saved on its own. When something feels worth keeping, &ldquo;Write this as me&rdquo; turns it into a private thought &mdash; you review it first.</p>
    </section>
  ` : "";
  const starterHtml = !page ? `
    <button class="companion-if-helps" data-action="companion-starter-question" data-question="${escapeHtml(starterQuestion)}">
      <span>If it helps:</span>
      <strong>${escapeHtml(starterQuestion)}</strong>
    </button>
  ` : "";

  // One lower section — iOS threadsSection parity: at most three returning
  // threads, each just a title and when it came back.
  const threads = patterns.filter((pattern) => pattern.id !== leadId).slice(0, 3);
  const threadsHtml = threads.length > 0 ? `
    <section class="companion-threads">
      <div class="companion-eyebrow">Keeps coming back</div>
      ${threads.map((pattern) => {
        const sortedIds = sortedPatternEntryIds(pattern);
        const latestEntryId = sortedIds[sortedIds.length - 1] || "";
        const lastSeen = pattern.lastSeenAt ? toRelativeDate(pattern.lastSeenAt) : "";
        return `
          <button
            class="companion-thread-row"
            data-action="${latestEntryId ? "open-companion" : "open-standalone-companion"}"
            ${latestEntryId ? `data-note-id="${escapeHtml(latestEntryId)}"` : ""}
          >
            <span class="companion-thread-copy">
              <strong>${escapeHtml(patternDisplayTitle(pattern))}</strong>
              ${lastSeen ? `<span>Came back ${escapeHtml(lastSeen)}</span>` : ""}
            </span>
            <span class="companion-thread-chevron" aria-hidden="true">&rsaquo;</span>
          </button>
        `;
      }).join("")}
    </section>
  ` : "";

  return `
    <section class="panel companion-home">
      <div class="panel-header">
        <div>
          <h2>Companion</h2>
        </div>
      </div>
      ${emptyHtml}
      ${renderOnYourMind()}
      ${deckHtml}
      <div class="companion-entry-actions" role="group" aria-label="How Companion listens">
        ${([["talk", "Talk", "Just talk it through"], ["deeper", "Untangle this", "Understand it more clearly"], ["find_words", "Find the words", "Shape it into words"]] as const).map(([intent, label, hint]) => `
          <button class="companion-intent-chip${state.companionHomeIntent === intent ? " is-selected" : ""}" data-action="select-companion-home-intent" data-intent="${intent}" aria-pressed="${state.companionHomeIntent === intent}" title="${hint}">${label}</button>
        `).join("")}
      </div>
      ${companionTalkBar(state.companionHomeIntent === "deeper" ? "What do you want to understand?" : state.companionHomeIntent === "find_words" ? "What are you trying to say?" : "What's on your mind?")}
      ${starterHtml}
      ${threadsHtml}
    </section>
  `;
}

// ── renderActivityView ──
export function renderActivityView(): string {
  return `
    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>Activity</h2>
          <div class="muted">Notifications and updates that matter.</div>
        </div>
        <button class="icon-btn" data-action="refresh-activity" aria-label="Refresh activity" title="Refresh">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15.5-6.2L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15.5 6.2L3 16"/><path d="M3 21v-5h5"/></svg>
        </button>
      </div>

      ${state.activity.length === 0 ? `<div class="empty">No activity yet.</div>` : state.activity
        .map(
          (item) => `
          <article class="activity-card">
            <div class="row spread">
              <h4>${escapeHtml(activityTitle(item))}</h4>
              <span class="meta">${toRelativeDate(item.createdAt)}</span>
            </div>
            <div class="muted">${escapeHtml(activityMessage(item))}</div>
            <div class="actions">
              <button class="btn-secondary btn-sm" data-action="open-activity" data-activity-id="${item.id}">Open</button>
              ${item.read ? `<span class="badge">Read</span>` : `<span class="badge accent">Unread</span>`}
            </div>
          </article>
        `
        )
        .join("")}
    </section>
  `;
}

// ── renderYouView ──
// iOS parity: the 4th primary tab is a calm hub. Friends, Matches, Activity,
// and Settings stay fully functional as destinations reached from here.
export function renderYouView(): string {
  const username = state.profile?.username || "";
  const displayName = state.profile?.profile?.displayName || "";
  const avatarURL = state.profile?.profile?.avatarURL || "";
  const pendingRequests = state.friends.filter((friend) => friend.status === "pending_incoming").length;
  const unreadActivity = state.activity.filter((item) => !item.read).length;
  const unreadMatches = state.thoughtMatchConversations.reduce((sum, item) => sum + item.unreadCount, 0);

  const row = (tab: TabName, title: string, subtitle: string, badge: number) => `
    <button class="you-row" data-action="set-tab" data-tab="${tab}">
      <span class="you-row-copy">
        <strong>${escapeHtml(title)}</strong>
        ${subtitle ? `<span>${escapeHtml(subtitle)}</span>` : ""}
      </span>
      ${badge > 0 ? `<span class="you-row-badge">${badge}</span>` : ""}
      <span class="you-row-chevron" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </span>
    </button>
  `;

  return `
    <section class="panel you-panel">
      <div class="panel-header">
        <div>
          <h2>You</h2>
        </div>
      </div>

      <button class="you-profile" data-action="set-tab" data-tab="settings">
        ${profileAvatarHTML(avatarURL, displayName || username || "You", "you-profile-avatar")}
        <span class="you-profile-copy">
          <strong>${escapeHtml(displayName || (username ? `@${username}` : "Your profile"))}</strong>
          <span>${displayName && username ? `@${escapeHtml(username)}` : username ? "Edit your profile" : "Set up your profile"}</span>
        </span>
        <span class="you-row-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </span>
      </button>

      ${renderPlanStatus()}
      <div class="you-list you-portrait-entry">
        ${row("portrait", "Your portrait", "What LOT has noticed", state.portrait.enabled ? state.portrait.presentation.needsYourWordIds.length : 0)}
      </div>
      <div class="you-section-title">Your writing</div>
      <div class="you-list">
        ${row("saved", "Saved Thoughts", "What you kept from Discovery", 0)}
      </div>
      <div class="you-section-title">Connections</div>
      <div class="you-list">
        ${row("matches", state.portraitMatching.enabled ? "Portrait matches" : "Matches", state.portraitMatching.enabled ? "Someone whose portrait echoes yours" : "When a thought finds yours", unreadMatches)}
        ${row("friends", "Friends", "The people you trust with your thoughts", pendingRequests)}
        ${row("activity", "Activity", "What happened around your thoughts", unreadActivity)}
      </div>
      <div class="you-list">
        ${row("settings", "Settings", "Profile, encryption, and account", 0)}
      </div>
    </section>
  `;
}

// ── renderSavedView ──
// iOS parity: You → Saved Thoughts. Everything the person kept, whether or
// not Discovery still has it loaded.
export function renderSavedView(): string {
  const byId = new Map<string, SharedPost>();
  state.feedPosts.forEach((post) => byId.set(post.id, post));
  Object.values(state.savedPostsById).forEach((post) => {
    if (!byId.has(post.id)) byId.set(post.id, post);
  });
  const saved = Array.from(state.savedPostIds)
    .map((id) => byId.get(id))
    .filter((post): post is SharedPost => !!post)
    .sort((a, b) => (b.sharedAt?.getTime() || 0) - (a.sharedAt?.getTime() || 0));
  const pending = state.savedPostIds.size - saved.length;
  return `
    <section class="panel saved-view">
      <div class="panel-header">
        <div>
          <h2>Saved Thoughts</h2>
          <p class="panel-sub">What you kept from Discovery.</p>
        </div>
      </div>
      ${saved.length === 0 && pending === 0
        ? `<div class="empty empty-with-action"><strong>Nothing saved yet.</strong><span>Save a thought from Discovery and it will wait for you here.</span><button class="btn-primary btn-sm" data-action="set-tab" data-tab="feed">Browse Discovery</button></div>`
        : ""}
      ${saved.map((post) => renderPostCard(post, "shared")).join("")}
      ${pending > 0 ? `<div class="empty" style="margin-top:10px;">Loading ${pending} more…</div>` : ""}
    </section>
  `;
}

// ── renderSharedThoughtNotice ──
// A shared link the app could not open. Says why, in place, with a way in —
// never a silent toast and never a dead-end page.
export function renderSharedThoughtNotice(): string {
  const notice = state.sharedThoughtNotice;
  if (!notice) return "";
  const appLink = `lot://post/${encodeURIComponent(notice.postId)}`;
  const isSignIn = notice.reason === "signin";
  return `
    <div class="modal-backdrop">
      <div class="modal shared-notice" role="dialog" aria-modal="true" aria-labelledby="shared-notice-title">
        <div class="modal-drag-handle" aria-hidden="true"></div>
        <div class="modal-header">
          <h3 id="shared-notice-title">Thought</h3>
          <button class="btn-icon modal-close-btn" data-action="close-shared-notice" aria-label="Close" title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="modal-section shared-notice-body">
          <strong>${isSignIn ? "Sign in to read this thought." : "This thought isn’t available."}</strong>
          <p>${isSignIn
            ? "It may be shared with friends only. If it was shared with you, signing in opens it."
            : "It may be private, removed, or shared only with friends."}</p>
          <div class="shared-notice-actions">
            ${isSignIn ? `<button class="btn-primary" data-action="guest-sign-in">Sign in</button>` : ""}
            <a class="btn-secondary" href="${escapeHtml(appLink)}">Open in LOT</a>
            <button class="btn-ghost" data-action="close-shared-notice">Read recent thoughts</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ── renderSettingsView ──
export function renderSettingsView(): string {
  const displayName = state.profile?.profile?.displayName || "";
  const bio = state.profile?.profile?.bio || "";
  const username = state.profile?.username || "";
  const avatarURL = state.profile?.profile?.avatarURL || "";
  const prefs = state.notificationPrefs || {};
  const encryptionReady = state.noteEncryption.hasEnvelope && state.noteEncryption.unlocked;
  const encryptionNeedsSetup = !state.noteEncryption.hasEnvelope;
  const deviceLabel = nativeDeviceLabel();
  const platformList = nativePlatformList();
  const encryptionStatusLabel = encryptionReady ? "Ready" : (encryptionNeedsSetup ? "Setup needed" : "Locked");
  const encryptionStatusClass = encryptionReady ? "ready" : (encryptionNeedsSetup ? "setup" : "locked");
  const encryptionTitle = encryptionReady
    ? "Encrypted notes are ready"
    : (encryptionNeedsSetup ? "Set up encrypted notes" : "Unlock encrypted notes");
  const encryptionSubtitle = encryptionReady
    ? (state.noteEncryption.remembered
      ? `This ${deviceLabel} can unlock encrypted notes automatically.`
      : `This ${deviceLabel} is unlocked and ready to read or create encrypted notes.`)
    : (encryptionNeedsSetup
      ? `Create one passphrase for ${platformList}. LOT never stores the passphrase itself.`
      : `Enter your passphrase once on this ${deviceLabel} to read and write encrypted notes.`);

  return `
    <section class="panel">
      <div class="panel-header">
        <div>
          <h2>Settings</h2>
          <div class="muted">Profile, notifications, and account controls.</div>
        </div>
      </div>

      ${isNativeAppShell() && encryptionNeedsSetup ? `
        <div class="native-setup-card">
          <div>
            <strong>Create your note passcode</strong>
            <span>This is required before writing encrypted notes or shared thoughts on Android.</span>
          </div>
          <a class="btn-primary btn-sm" href="#note-encryption-settings">Create</a>
        </div>
      ` : ""}

      <form id="profile-settings-form" class="settings-section">
        <h3>Profile</h3>
        <div class="profile-preview-web">
          ${profileAvatarHTML(avatarURL, username || displayName, "profile-avatar-lg")}
          <div>
            <strong>${escapeHtml(username ? `@${username}` : "Set your username")}</strong>
            <span>${escapeHtml(displayName || "Add a display name")}</span>
            ${bio ? `<p>${escapeHtml(bio)}</p>` : ""}
          </div>
        </div>
        <div class="grid-2" style="margin-top:12px;">
          <div>
            <label>Username</label>
            <input name="username" value="${escapeHtml(username)}" placeholder="username" />
          </div>
          <div>
            <label>Display Name</label>
            <input name="displayName" value="${escapeHtml(displayName)}" placeholder="Display name" />
          </div>
        </div>
        <div style="margin-top:12px;">
          <label>Bio</label>
          <textarea name="bio" maxlength="160">${escapeHtml(bio)}</textarea>
        </div>
        <div class="profile-avatar-editor">
          <label>Avatar</label>
          <input name="avatar" type="file" accept="image/*" />
          ${avatarURL ? `
            <label class="checkbox-row">
              <input name="removeAvatar" type="checkbox" />
              <span>Remove current avatar</span>
            </label>
          ` : ""}
          <div class="meta">Images must be under 3 MB.</div>
        </div>
        <div class="row spread" style="margin-top:12px;">
          <span class="meta">Username can be changed here.</span>
          <button class="btn-primary" type="submit" ${isBusy("save-profile") ? "disabled" : ""}>Save Profile</button>
        </div>
      </form>

      <div class="settings-section">
        <h3>Notification Preferences</h3>
        ${[
          ["friendPost", "Friend posts"],
          ["friendRequest", "Friend requests"],
          ["dailyPrompt", "Daily prompt reminder"],
        ]
          .map(
            ([key, label]) => `
            <label class="pref-row">
              <span>${escapeHtml(label)}</span>
              <span class="switch">
                <input type="checkbox" data-action="toggle-pref" data-pref-key="${key}" ${(prefs as Record<string, boolean>)[key] ? "checked" : ""} />
                <span class="switch-ui"></span>
              </span>
            </label>
          `
          )
          .join("")}
      </div>

      <div id="note-encryption-settings" class="settings-section">
        <h3>Note Encryption</h3>
        <div class="encryption-hero">
          <div class="encryption-icon ${encryptionStatusClass}" aria-hidden="true">${encryptionReady ? "OK" : (encryptionNeedsSetup ? "KEY" : "LOCK")}</div>
          <div class="encryption-hero-copy">
            <div class="row spread">
              <strong>${escapeHtml(encryptionTitle)}</strong>
              <span class="encryption-pill ${encryptionStatusClass}">${escapeHtml(encryptionStatusLabel)}</span>
            </div>
            <p>${escapeHtml(encryptionSubtitle)}</p>
          </div>
        </div>
        <div class="encryption-help">
          ${escapeHtml(`Cross-platform encryption is the recommended path for ${platformList}. Use a memorable passphrase and save it somewhere safe.`)}
        </div>
        ${state.noteEncryption.hasLegacyIOSNotes && !state.noteEncryption.hasEnvelope ? `
          <div class="setting-note" style="margin-top:12px;">
            Older iPhone-only encrypted notes were detected. Finish the one-time migration on iPhone first, then come back here to unlock them safely.
          </div>
        ` : ""}
        ${!state.noteEncryption.hasEnvelope ? `
          <div class="grid-2 encryption-fields">
            <div>
              <label>Create passphrase</label>
              <input type="password" name="cross-platform-passphrase" placeholder="At least 8 characters" />
            </div>
            <div>
              <label>Confirm</label>
              <input type="password" name="cross-platform-confirm" placeholder="Repeat passphrase" />
            </div>
          </div>
          <label class="pref-row encryption-toggle">
            <span>${escapeHtml(`Remember this ${deviceLabel}`)}</span>
            <span class="switch">
              <input type="checkbox" name="cross-platform-remember" />
              <span class="switch-ui"></span>
            </span>
          </label>
          <div class="encryption-action-row">
            <span class="meta">${escapeHtml(`This saves the encrypted-note key on this ${deviceLabel}, not the passphrase itself.`)}</span>
            <button class="btn-primary" data-action="setup-cross-platform-encryption">Create encryption passphrase</button>
          </div>
        ` : (!state.noteEncryption.unlocked ? `
          <div class="encryption-fields">
            <label>${escapeHtml(`Unlock on this ${deviceLabel}`)}</label>
            <input type="password" name="cross-platform-unlock" placeholder="Enter your passphrase" />
          </div>
          <label class="pref-row encryption-toggle">
            <span>${escapeHtml(`Remember this ${deviceLabel}`)}</span>
            <span class="switch">
              <input type="checkbox" name="cross-platform-unlock-remember" />
              <span class="switch-ui"></span>
            </span>
          </label>
          <div class="encryption-action-row">
            <span class="meta">Use this on personal devices only. You can forget the saved unlock later.</span>
            <button class="btn-primary" data-action="unlock-cross-platform-encryption">${escapeHtml(`Unlock this ${deviceLabel}`)}</button>
          </div>
        ` : `
          <div class="setting-note" style="margin-top:12px;">
            ${escapeHtml(`New notes created here stay encrypted in the cross-platform format and can be opened on ${platformList}.`)}
          </div>
          ${state.noteEncryption.remembered ? `
            <div class="encryption-action-row">
              <span class="meta">${escapeHtml(`This ${deviceLabel} has a saved encrypted-note unlock.`)}</span>
              <button class="btn-secondary btn-sm" data-action="forget-cross-platform-unlock">Forget saved unlock</button>
            </div>
          ` : ""}
        `)}
        ${state.noteEncryption.hasEnvelope ? `
          <details class="encryption-details">
            <summary>
              <span>Change passphrase</span>
              <span>Use this if you want a new unlock phrase.</span>
            </summary>
            <div class="grid-2 encryption-fields">
              <div>
                <label>Current passphrase</label>
                <input type="password" name="cross-platform-current" placeholder="Current passphrase" />
              </div>
              <div>
                <label>New passphrase</label>
                <input type="password" name="cross-platform-new" placeholder="At least 8 characters" />
              </div>
            </div>
            <div class="encryption-fields">
              <label>Confirm new passphrase</label>
              <input type="password" name="cross-platform-new-confirm" placeholder="Repeat new passphrase" />
            </div>
            <label class="pref-row encryption-toggle">
              <span>${escapeHtml(`Remember this ${deviceLabel} after change`)}</span>
              <span class="switch">
                <input type="checkbox" name="cross-platform-change-remember" ${state.noteEncryption.remembered ? "checked" : ""} />
                <span class="switch-ui"></span>
              </span>
            </label>
            <div class="encryption-action-row">
              <span class="meta">${escapeHtml(`Changes the passphrase used on ${platformList}. Existing notes stay readable.`)}</span>
              <button class="btn-secondary" data-action="change-cross-platform-passphrase">Update passphrase</button>
            </div>
          </details>
        ` : ""}
      </div>

      <div class="settings-section">
        <h3>AI Reflection</h3>
        <label class="pref-row" style="margin-top:12px;">
          <span>Enable AI reflection for notes</span>
          <span class="switch">
            <input type="checkbox" data-action="toggle-notes-ai" ${state.aiPrefs.notesAiEnabled ? "checked" : ""} />
            <span class="switch-ui"></span>
          </span>
        </label>
        <div class="meta" style="margin-top:8px;">
          This device sends only your note text for analysis. Plain note text is not saved in AI documents, and AI metadata stays private to your account and never enters public or community matching.
        </div>
        ${state.portraitMatching.enabled ? `
        <label class="pref-row" style="margin-top:12px;">
          <span>Let your portrait look for someone</span>
          <span class="switch">
            <input type="checkbox" data-action="portrait-match-consent-toggle" ${state.portraitMatching.consent ? "checked" : ""} />
            <span class="switch-ui"></span>
          </span>
        </label>
        <div class="meta" style="margin-top:8px;">Reflection does not enable portrait matching. You choose that separately. Portrait matching compares only observations you confirmed, never sensitive ones — and nobody sees your words unless you share them yourself.</div>
        ` : ""}
        <button class="btn-secondary btn-sm" style="margin-top:12px;" data-action="set-tab" data-tab="portrait">Open Your portrait</button>
      </div>

      <div class="settings-section companion-memory-settings">
        <div class="row spread">
          <div>
            <h3>What LOT remembers</h3>
            <div class="meta">Encrypted summaries you approved. Raw Companion chats are not saved.</div>
          </div>
          ${state.companionContext.loading ? `<span class="meta">Loading…</span>` : ""}
        </div>
        ${!state.noteEncryption.unlocked ? `
          <div class="setting-note" style="margin-top:12px;">Unlock note encryption to view or change Companion memory.</div>
        ` : state.companionContext.portrait ? `
          <div class="companion-portrait-settings">
            <strong>A quiet understanding of you</strong>
            <div class="companion-portrait-lines">
              ${companionPortraitLines(state.companionContext.portrait.summary)
                .map((line) => `<p>${escapeHtml(line)}</p>`)
                .join("")}
            </div>
            <label for="companion-guidance">Correct what LOT should keep in mind</label>
            <textarea id="companion-guidance" data-field="companion-guidance" placeholder="Add a correction in your own words.">${escapeHtml(state.companionContext.portrait.userGuidance || "")}</textarea>
            <div class="companion-portrait-actions">
              <button class="btn-primary btn-sm" data-action="save-companion-guidance">Save correction</button>
              <button class="btn-danger btn-sm" data-action="delete-companion-portrait">Delete this understanding</button>
            </div>
          </div>
        ` : `
          <div class="setting-note" style="margin-top:12px;">Nothing has been saved to Companion memory yet.</div>
        `}
        ${state.companionContext.memories.length > 0 ? `
          <div class="companion-memory-list">
            ${state.companionContext.memories.map((memory) => `
              <article class="companion-memory-item">
                <div>
                  <strong>Remembered privately</strong>
                  <p>${escapeHtml(memory.summary)}</p>
                  ${memory.nextQuestion ? `<span>${escapeHtml(memory.nextQuestion)}</span>` : ""}
                </div>
                <button class="btn-danger btn-sm" data-action="delete-companion-memory" data-memory-id="${escapeHtml(memory.id)}">Delete</button>
              </article>
            `).join("")}
          </div>
        ` : ""}
      </div>

      <div class="settings-section">
        <h3>Safety & Support</h3>
        <div class="meta" style="margin-top:8px;">
          LOT has no tolerance for objectionable content or abusive users. Safety filters block clearly objectionable text before posting, and public posts include Hide, Report, and Block controls. Reports are reviewed within 24 hours, and confirmed violations may lead to content removal and account suspension or termination.
        </div>
        <div class="row wrap" style="margin-top:12px;">
          <a class="btn-secondary btn-sm" href="https://superable.net/contact" target="_blank" rel="noreferrer">Contact Safety</a>
          <a class="btn-secondary btn-sm" href="https://superable.net/lot/termofuse" target="_blank" rel="noreferrer">Community Rules</a>
        </div>
      </div>

      <div class="settings-section">
        <h3>About LOT</h3>
        <div class="about-lot-copy">
          <p>LOT is a private place to think out loud, talk through what is hard to name, and understand what keeps returning.</p>
          <p>Write or speak naturally. Companion can stay conversational, go deeper when you ask, and turn a meaningful exchange into your own words.</p>
          <p>Your thoughts remain yours. You decide what LOT remembers, what stays private, and what you share.</p>
        </div>
        <a class="btn-secondary btn-sm" href="https://superable.net/lot" target="_blank" rel="noreferrer">Learn more</a>
      </div>

      <div class="settings-section">
        <h3>Legal</h3>
        <div class="row wrap" style="margin-top:12px;">
          <a class="btn-secondary btn-sm" href="https://superable.net/lot/termofuse" target="_blank" rel="noreferrer">Terms of Use</a>
          <a class="btn-secondary btn-sm" href="https://superable.net/lot/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>
          <a class="btn-secondary btn-sm" href="https://discord.gg/c7nUxaZtrA" target="_blank" rel="noreferrer">Discord</a>
        </div>
      </div>

      <div class="settings-section">
        <h3>Account</h3>
        <div class="row wrap" style="margin-top:12px;">
          <button class="btn-secondary" data-action="sign-out">Sign Out</button>
          <button class="btn-danger" data-action="delete-account" ${isBusy("delete-account") ? "disabled" : ""}>Delete Account</button>
        </div>
      </div>
    </section>
  `;
}

// ── renderMainShell ──
function renderRightRail(isGuest: boolean): string {
  if (isGuest) {
    return `
      <aside class="right-rail" aria-label="LOT overview">
        <section class="rail-card rail-card-cta">
          <div class="rail-kicker">What LOT is for</div>
          <h3>Write what is real. See what connects.</h3>
          <p>Public thoughts are open to read. Your private writing stays encrypted.</p>
        </section>
      </aside>
    `;
  }

  const railCards: string[] = [];

  if (state.activeTab === "notes") {
    // The Companion door now lives inside the Thoughts panel itself (iOS
    // parity) — the rail keeps only the pattern doorway.
    const patternDoorway = renderPatternDoorway();
    if (patternDoorway) railCards.push(patternDoorway);
  }

  if (state.activeTab === "matches") {
    railCards.push(`
      <section class="rail-card rail-card-cta">
        <div class="rail-kicker">Matches</div>
        <h3>You are not alone starts with real overlap.</h3>
        <p>LOT only shows strong matches. Similar thoughts can lead to resonance, then anonymous conversation if both people choose it.</p>
      </section>
    `);
  }

  // Settings has no right rail — legal/about links live in the Settings page
  // itself (Legal section), so the screen has one clear hero.

  if (railCards.length === 0) return "";

  return `
    <aside class="right-rail" aria-label="Context">
      ${railCards.join("")}
    </aside>
  `;
}

function friendshipActionForProfile(profile: PublicProfile): string {
  if (!state.user) {
    return `<button class="btn-primary btn-sm" data-action="guest-sign-in">Sign In to connect</button>`;
  }
  if (isOwnUid(profile.uid)) {
    return `<a class="btn-secondary btn-sm" href="/app/settings" data-action="set-tab" data-tab="settings">Edit profile</a>`;
  }
  const relation = state.friends.find((friend) => friend.friendUid === profile.uid);
  if (relation?.status === "accepted") {
    return `<span class="friends-status-chip">Friends</span>`;
  }
  if (relation?.status === "pending_outgoing") {
    return `<span class="friends-status-chip">Request sent</span>`;
  }
  if (relation?.status === "pending_incoming") {
    return `<button class="btn-primary btn-sm" data-action="accept-friend" data-uid="${escapeHtml(profile.uid)}" ${isBusy(`friend-accept-${profile.uid}`) ? "disabled" : ""}>Accept</button>`;
  }
  return `<button class="btn-primary btn-sm" data-action="send-friend-request" data-uid="${escapeHtml(profile.uid)}" ${isBusy(`friend-send-${profile.uid}`) ? "disabled" : ""}>Add friend</button>`;
}

function renderPublicProfileOverlay(): string {
  const profile = state.publicProfile;
  if (!profile) return "";
  const postCount = profile.posts.length;
  return `
    <div class="profile-overlay" role="dialog" aria-modal="true" aria-label="${escapeHtml(profile.displayName || profile.username)} profile">
      <section class="profile-sheet" data-scroll-preserve="public-profile-sheet">
        <div class="profile-sheet-cover">
          <button class="profile-sheet-close" data-action="close-public-profile" aria-label="Close profile">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="1" y1="1" x2="13" y2="13"/>
              <line x1="13" y1="1" x2="1" y2="13"/>
            </svg>
          </button>
        </div>
        <div class="profile-sheet-header">
          ${profileAvatarHTML(profile.avatarURL, profile.displayName || profile.username, "profile-sheet-avatar")}
          <div class="profile-sheet-actions">${friendshipActionForProfile(profile)}</div>
        </div>
        <div class="profile-sheet-copy">
          <h2>${escapeHtml(profile.displayName || profile.username)}</h2>
          <div class="profile-sheet-handle">@${escapeHtml(profile.username)}</div>
          ${profile.bio ? `<p>${escapeHtml(profile.bio)}</p>` : `<p class="muted">Writing public thoughts on LOT.</p>`}
          <div class="profile-sheet-stats">
            <span><strong>${postCount}</strong> public ${postCount === 1 ? "thought" : "thoughts"}</span>
          </div>
        </div>
        <div class="profile-sheet-tabs">
          <span class="active">Posts</span>
        </div>
        <div class="profile-sheet-posts">
          ${profile.posts.length === 0
            ? `<div class="empty">No public username-attributed thoughts yet.</div>`
            : profile.posts.map((post) => renderPostCard(post, "shared")).join("")}
        </div>
      </section>
    </div>
  `;
}

function isIOSVisitor(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function renderIOSInstallBanner(): string {
  if (state.user || !isIOSVisitor()) return "";
  return `
    <a class="ios-install-banner" href="${APP_STORE_URL}" target="_blank" rel="noreferrer">
      <img src="/assets/app-store-icon.png" alt="" class="ios-install-icon" />
      <span class="ios-install-copy">
        <strong>LOT for iPhone</strong>
        <span>Get the native app experience.</span>
      </span>
      <span class="ios-install-pill">Get</span>
    </a>
  `;
}

function renderConfirmModal(): string {
  if (!state.confirmModal) return "";
  return `
    <div class="confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="confirm-msg">
      <div class="confirm-sheet">
        <p class="confirm-message" id="confirm-msg">${escapeHtml(state.confirmModal.message)}</p>
        <div class="confirm-actions">
          <button class="btn-secondary" data-action="confirm-no">Cancel</button>
          <button class="btn-danger" data-action="confirm-yes">Confirm</button>
        </div>
      </div>
    </div>
  `;
}

function renderCompanionModal(): string {
  const session = state.companionSession;
  if (!session) return "";
  // Mode selection still runs underneath (the server auto-shifts modes
  // between turns) — it just has no visible chrome, matching iOS.
  const allMessages = [
    { id: "opening", role: "ai" as const, content: session.openingMessage, createdAt: new Date() },
    ...session.messages,
  ];
  if (session.finishing) {
    return `<div class="companion-backdrop" role="dialog" aria-modal="true" aria-labelledby="finish-title">
      <section class="companion-sheet companion-journal-sheet companion-finish-sheet">
        <h2 id="finish-title">Finish for now</h2>
        <p>Save a short memory only if you choose. This conversation is not stored by LOT.</p>
        ${session.isPreparingMemory ? `<p role="status">Finding what mattered…</p>` : ""}
        ${session.memoryDraft ? `<label>Remember for next time<textarea class="companion-journal-editor" data-field="companion-memory-summary" maxlength="700">${escapeHtml(session.memoryDraft.summary)}</textarea></label>
          <label>A question for next time (optional)<input data-field="companion-memory-question" maxlength="220" value="${escapeHtml(session.memoryDraft.nextQuestion || "")}" /></label>` : ""}
        ${session.error ? `<p role="alert">${escapeHtml(session.error)}</p>` : ""}
        <div class="companion-journal-actions">
          ${session.memoryDraft ? `<button class="btn-primary" data-action="save-companion-memory" ${session.isPreparingMemory || isBusy("save-companion-memory") || !session.memoryDraft.summary.trim() ? "disabled" : ""}>Remember and finish</button>` : ""}
          <button class="btn-secondary" data-action="finish-without-memory" ${isBusy("save-companion-memory") ? "disabled" : ""}>Finish without saving</button>
          <button class="btn-secondary" data-action="keep-talking" ${isBusy("save-companion-memory") ? "disabled" : ""}>Keep talking</button>
        </div>
      </section></div>`;
  }
  if (session.journalDraft) {
    return `
      <div class="companion-backdrop" role="dialog" aria-modal="true" aria-labelledby="companion-journal-title">
        <section class="companion-sheet companion-journal-sheet">
          <header class="companion-header">
            <div class="companion-title-block">
              <h2 id="companion-journal-title">In my words</h2>
              <div class="meta">Edit this until it sounds like you.</div>
            </div>
            <button class="btn-icon" data-action="discard-companion-journal" aria-label="Close reflection">×</button>
          </header>
          ${session.journalDraft.requiresCarefulReview ? `
            <div class="companion-review-note">Some details were uncertain, so read this carefully before saving.</div>
          ` : ""}
          <textarea
            class="companion-journal-editor"
            data-field="companion-journal"
            aria-label="Editable first person reflection"
          >${escapeHtml(session.journalDraft.body)}</textarea>
          <div class="companion-journal-actions">
            <button class="btn-secondary" data-action="discard-companion-journal">Keep talking</button>
            <button class="btn-primary" data-action="save-companion-journal" ${isBusy("save-companion-journal") ? "disabled" : ""}>Save as private note</button>
          </div>
        </section>
      </div>
    `;
  }
  return `
    <div class="companion-backdrop" role="dialog" aria-modal="true" aria-labelledby="companion-title">
      <section class="companion-sheet">
        <header class="companion-header">
          <span class="companion-header-orb" aria-hidden="true">
            <img src="/assets/Companion_LOT.svg" alt="" />
          </span>
          <div class="companion-title-block">
            <h2 id="companion-title">Companion</h2>
            <div class="meta">Private · remembered only if you choose</div>
          </div>
          <button class="btn-icon" data-action="close-companion" aria-label="Finish for now" ${session.isSending ? "disabled" : ""}>×</button>
          <div class="companion-intent-seg" role="tablist" aria-label="Conversation depth">
            <button
              class="companion-intent-seg-btn ${session.conversationIntent === "talk" ? "active" : ""}"
              data-action="set-companion-intent"
              data-intent="talk"
              role="tab"
              aria-selected="${session.conversationIntent === "talk" ? "true" : "false"}"
            >Just talk</button>
            <button
              class="companion-intent-seg-btn ${session.conversationIntent === "deeper" ? "active" : ""}"
              data-action="set-companion-intent"
              data-intent="deeper"
              role="tab"
              aria-selected="${session.conversationIntent === "deeper" ? "true" : "false"}"
            >Untangle this</button>
            <button class="companion-intent-seg-btn ${session.conversationIntent === "find_words" ? "active" : ""}" data-action="set-companion-intent" data-intent="find_words" role="tab" aria-selected="${session.conversationIntent === "find_words"}">Find the words</button>
          </div>
        </header>
        <section class="companion-chat" aria-label="Companion conversation">
          <div class="companion-messages">
            ${session.risk?.level === "high" ? `
              <div class="companion-crisis-card">
                <strong>Immediate support is available</strong>
                <span>LOT is not emergency support. In the US, call or text 988. If you may be in immediate danger, contact local emergency services.</span>
                <a href="tel:988">Call 988</a>
              </div>
            ` : ""}
            ${allMessages.map((message) => message.role === "user" ? `
              <div class="companion-msg user"><div class="companion-user-bubble">${escapeHtml(message.content)}</div></div>
            ` : `
              <div class="companion-msg ai">${escapeHtml(message.content)}</div>
            `).join("")}
            ${session.conversationIntent === "deeper" && session.deepInsight ? `
              <div class="companion-insight-inline">${escapeHtml(session.deepInsight)}</div>
            ` : ""}
            ${session.isSending ? `<div class="companion-msg ai typing">Staying with this…</div>` : ""}
            ${session.error ? `<div class="companion-error">${escapeHtml(session.error)}</div>` : ""}
            ${(session.shouldOfferJournalDraft || session.messages.filter((message) => message.role === "user").length >= 2) ? `
              <div class="companion-after-actions">
                <button class="companion-quiet-action" data-action="prepare-companion-journal" ${session.isPreparingJournal ? "disabled" : ""}>
                  ${session.isPreparingJournal ? "Writing in your voice…" : "Put this into my words"}
                </button>
              </div>
            ` : ""}
            ${session.memoryDraft ? `
              <section class="companion-memory-offer">
                <strong>Remember what mattered here?</strong>
                <span>LOT will save a short encrypted understanding, not this conversation.</span>
                <p>${escapeHtml(session.memoryDraft.summary)}</p>
                <div class="row">
                  <button class="btn-secondary btn-sm" data-action="dismiss-companion-memory">Not now</button>
                  <button class="btn-primary btn-sm" data-action="save-companion-memory" ${isBusy("save-companion-memory") ? "disabled" : ""}>Remember</button>
                </div>
              </section>
            ` : ""}
          </div>
        </section>
        <footer class="companion-input-shell">
          <div class="companion-input-row">
            <textarea data-field="companion-input" placeholder="${session.conversationIntent === "deeper" ? "Say what you want to understand more clearly…" : "Say whatever is on your mind…"}" rows="1">${escapeHtml(session.input)}</textarea>
            <button
              class="companion-voice ${session.isListening ? "active" : ""}"
              data-action="toggle-companion-voice"
              aria-label="${session.isListening ? "Stop voice input" : "Start voice input"}"
              title="${session.isListening ? "Stop voice input" : "Voice input"}"
            >${session.isListening
              ? `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6.5" y="6.5" width="11" height="11" rx="2.5"/></svg>`
              : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`}</button>
            <button class="companion-send" data-action="send-companion-message" aria-label="Send" ${session.input.trim() && !session.isSending ? "" : "disabled"}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
            </button>
          </div>
        </footer>
      </section>
    </div>
  `;
}

function renderThoughtMatchExcerptReview(): string {
  const draft = state.thoughtMatchExcerptDraft;
  if (!draft) return "";
  const count = draft.excerpt.trim().length;
  const valid = count >= 40 && count <= 400;
  return `
    <div class="companion-backdrop" role="dialog" aria-modal="true" aria-labelledby="match-review-title">
      <section class="match-review-sheet">
        <header class="match-review-header">
          <div>
            <h2 id="match-review-title">Anonymous excerpt</h2>
            <p>This is a shorter, complete version of your thought. Only a possible match can see it.</p>
          </div>
          <button class="btn-icon" data-action="dismiss-match-excerpt" aria-label="Not now">×</button>
        </header>
        <div class="match-review-body">
          <h3>Share only what feels safe</h3>
          <p>Your original thought stays private and encrypted. Review the anonymous version below before it starts looking.</p>
          <textarea data-field="match-excerpt" maxlength="400">${escapeHtml(draft.excerpt)}</textarea>
          ${draft.wasAnonymized ? `<div class="match-review-info good">✓ Names and identifying details were replaced automatically. Please review it once.</div>` : ""}
          ${draft.containsIdentifyingInfo ? `<div class="match-review-info warning">⚠ Some identifying details may remain. Edit them before continuing.</div>` : ""}
          <div class="match-review-count ${valid ? "" : "invalid"}">
            <span>${count < 40 ? "Add a little more so the meaning is clear." : ""}</span>
            <span>${count}/400</span>
          </div>
          ${state.thoughtMatchExcerptError ? `<div class="companion-error">${escapeHtml(state.thoughtMatchExcerptError)}</div>` : ""}
        </div>
        <footer class="match-review-actions">
          <button class="btn-secondary" data-action="dismiss-match-excerpt">Not now</button>
          <button class="btn-primary" data-action="approve-match-excerpt" ${!valid || isBusy("approve-match-excerpt") ? "disabled" : ""}>${isBusy("approve-match-excerpt") ? "Starting…" : "Start looking"}</button>
        </footer>
      </section>
    </div>
  `;
}

function renderMatchExcerptModal(): string {
  const detail = state.selectedMatchExcerpt;
  if (!detail) return "";
  return `
    <div class="companion-backdrop" role="dialog" aria-modal="true" aria-labelledby="match-excerpt-title">
      <section class="match-detail-sheet">
        <header>
          <h2 id="match-excerpt-title">${escapeHtml(detail.title)}</h2>
          <button class="btn-icon" data-action="close-match-excerpt" aria-label="Done">×</button>
        </header>
        <div>${escapeHtml(detail.text)}</div>
      </section>
    </div>
  `;
}

function renderThoughtMatchConversationModal(): string {
  const conversation = state.thoughtMatchConversations.find(
    (item) => item.id === state.selectedMatchConversationId,
  );
  if (!conversation) return "";
  return `
    <div class="companion-backdrop" role="dialog" aria-modal="true" aria-labelledby="match-chat-title">
      <section class="match-chat-sheet">
        <header class="match-chat-header">
          <button class="btn-secondary btn-sm" data-action="close-match-conversation">Close</button>
          <h2 id="match-chat-title">The other person</h2>
          <div class="match-chat-menu">
            <button class="btn-icon" data-action="report-match-conversation" aria-label="Report conversation">!</button>
            <button class="btn-icon danger" data-action="leave-match-conversation" data-block="false" aria-label="Leave conversation">×</button>
          </div>
        </header>
        <div class="match-chat-scroll">
          <section class="match-chat-context">
            <strong>${escapeHtml(conversation.explanation)}</strong>
            <span>${escapeHtml(conversation.otherExcerpt)}</span>
          </section>
          <div class="match-chat-messages">
            ${state.thoughtMatchMessages.map((message) => {
              const mine = message.senderSlot === conversation.mySlot;
              return `
                <div class="match-message ${mine ? "mine" : "theirs"}">
                  <small>${mine ? "You" : "The other person"}</small>
                  <span>${escapeHtml(message.text)}</span>
                </div>
              `;
            }).join("")}
          </div>
        </div>
        <footer class="match-chat-composer">
          <textarea data-field="match-message" rows="1" placeholder="Say what this brought up">${escapeHtml(state.thoughtMatchInput)}</textarea>
          <button class="btn-primary" data-action="send-match-message" ${!state.thoughtMatchInput.trim() || isBusy("send-match-message") ? "disabled" : ""}>↑</button>
        </footer>
      </section>
    </div>
  `;
}

export function renderMainShell(): string {
  const isGuest = state.guestMode && !state.user;
  const nativeShell = isNativeAppShell();

  let content = "";
  switch (state.activeTab) {
    case "feed":        content = renderFeedView(); break;
    case "write":       content = isGuest ? renderGuestGateView("write") : renderWriteView(); break;
    case "matches":     content = isGuest ? renderGuestGateView("matches") : renderMatchesView(); break;
    case "companion":   content = isGuest ? renderGuestGateView("companion") : renderCompanionView(); break;
    case "friends":     content = isGuest ? renderGuestGateView("friends") : renderFriendsView(); break;
    case "notes":       content = isGuest ? renderGuestGateView("notes") : renderNotesView(); break;
    case "activity":    content = isGuest ? renderGuestGateView("activity") : renderActivityView(); break;
    case "settings":    content = isGuest ? renderGuestGateView("settings") : renderSettingsView(); break;
    case "you":         content = isGuest ? renderGuestGateView("you") : renderYouView(); break;
    case "saved":       content = isGuest ? renderGuestGateView("you") : renderSavedView(); break;
    case "portrait":    content = isGuest ? renderGuestGateView("you") : renderPortraitView(); break;
    default:            content = renderFeedView();
  }

  const unreadCount = state.activity.filter((item) => !item.read).length;
  const userDisplay = displayNameForProfile(state.profile);

  const navIcons: Record<string, string> = {
    feed: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
    write: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>`,
    matches: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="7" cy="12" r="3"/><circle cx="17" cy="12" r="3"/><path d="M10 12h4"/><path d="M4.5 7.5l2-2 2 2"/><path d="M15.5 16.5l2 2 2-2"/></svg>`,
    companion: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a7 7 0 0 0-7 7c0 4.5 4.2 7.1 6.2 10.2a.9.9 0 0 0 1.6 0C14.8 17.1 19 14.5 19 10a7 7 0 0 0-7-7Z"/><path d="M9.5 10.5h.01"/><path d="M14.5 10.5h.01"/><path d="M9.5 14c1.2.8 3.8.8 5 0"/></svg>`,
    friends: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>`,
    notes: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`,
    activity: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>`,
    settings: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>`,
    you: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
  };

  // Secondary destinations (friends/matches/activity/settings/write) highlight
  // their primary parent so the 4-tab nav never loses its selection.
  const activePrimaryTab: TabName = PRIMARY_TAB_FOR[state.activeTab] || state.activeTab;
  const matchUnread = state.thoughtMatchConversations.reduce((sum, item) => sum + item.unreadCount, 0);
  const pendingFriendRequests = state.friends.filter((friend) => friend.status === "pending_incoming").length;
  // The You tab carries the personal-inbox badge (activity + matches + requests).
  const youBadgeCount = unreadCount + matchUnread + pendingFriendRequests;

  const sidebarItems = PRIMARY_NAV_TABS.map((tab) => {
    const isActive = activePrimaryTab === tab;
    const label = TAB_LABELS[tab];
    const badge = tab === "you" && youBadgeCount > 0
      ? `<span class="sidebar-badge">${youBadgeCount}</span>`
      : "";
    return `
      <li class="sidebar-item sidebar-tab-${escapeHtml(tab)} ${isActive ? "active" : ""}" data-tab="${escapeHtml(tab)}">
        <a href="${pathForTab(tab)}" data-action="set-tab" data-tab="${tab}">
          <span class="sidebar-icon">${navIcons[tab] || ""}</span>
          ${escapeHtml(label)}
          ${badge}
        </a>
      </li>`;
  }).join("");

  const bottomTabs = PRIMARY_NAV_TABS.map((tab) => {
    const isActive = activePrimaryTab === tab;
    const label = TAB_LABELS[tab];
    const dot = tab === "you" && youBadgeCount > 0
      ? `<span class="bottom-nav-dot"></span>`
      : "";
    return `
      <a class="bottom-nav-item ${isActive ? "active" : ""}" href="${pathForTab(tab)}" data-action="set-tab" data-tab="${tab}">
        ${dot}
        <span class="bottom-nav-icon">${navIcons[tab] || ""}</span>
        <span class="bottom-nav-label">${escapeHtml(label)}</span>
      </a>`;
  }).join("");

  const topbarRight = isGuest
    ? `<button class="btn-primary btn-sm topbar-signin-btn" data-action="guest-sign-in">Sign In</button>`
    : `<button class="user-pill user-pill-rich" data-action="set-tab" data-tab="settings">
         ${profileAvatarHTML(state.profile?.profile?.avatarURL, userDisplay, "user-pill-avatar")}
         <span>${escapeHtml(userDisplay)}</span>
       </button>
       ${nativeShell ? "" : `<button class="topbar-signout" data-action="sign-out">Sign Out</button>`}`;

  return `
    <div class="app-frame">
      <header class="topbar ${nativeShell ? "topbar-native" : ""}">
        <div class="topbar-inner">
          <a class="topbar-brand" href="/app" data-action="set-tab" data-tab="feed" aria-label="Open LOT">
            <img src="/assets/app-store-icon.png" alt="LOT" class="topbar-icon" />
            <span class="topbar-title">Language of Thoughts</span>
          </a>
          <div class="topbar-right">
            ${topbarRight}
          </div>
        </div>
      </header>
      ${renderIOSInstallBanner()}

      <div class="app-body">
        <nav class="sidebar" aria-label="Main navigation">
          <ul class="sidebar-list">
            ${sidebarItems}
          </ul>
        </nav>

        <main class="main-content" id="main-content">
          ${state.loadingPage ? `<div class="app-loading-line" role="progressbar" aria-label="Loading LOT"></div>` : ""}
          ${content}
          <footer class="app-footer small muted">© 2026 Super Able Inc.</footer>
        </main>
        ${renderRightRail(isGuest)}
      </div>

      <nav class="bottom-nav" aria-label="Tab bar">
        <div class="bottom-nav-items">${bottomTabs}</div>
      </nav>
    </div>

    ${renderThreadModal()}
    ${renderSharedThoughtNotice()}
    ${renderPortraitClaimModal()}
    ${renderPortraitNotQuiteDialog()}
    ${renderPublicProfileOverlay()}
    ${renderCompanionModal()}
    ${renderUpdateModal()}
    ${renderThoughtMatchExcerptReview()}
    ${renderMatchExcerptModal()}
    ${renderThoughtMatchConversationModal()}
    ${renderConfirmModal()}
  `;
}

// ── DOM render ─────────────────────────────────────────────────────────────
const root = document.getElementById("app");

export function render(): void {
  if (!root) return;

  if (state.loadingAuth) {
    morph(root, renderFullscreenLoading());
    return;
  }
  if (!state.user) {
    if (state.guestMode) {
      morph(root, renderMainShell());
      return;
    }
    morph(root, renderAuthView());
    return;
  }
  if (!state.profileResolved) {
    morph(root, renderFullscreenLoading());
    return;
  }
  if (!state.profile?.username) {
    morph(root, renderUsernameOnboarding());
    return;
  }
  // Required passphrase step — only once we know the real encryption state, and
  // never for legacy iOS accounts (those migrate on iPhone). Existing users can
  // defer for the session via "I'll set this up later".
  if (
    state.noteEncryptionResolved &&
    !state.noteEncryption.hasEnvelope &&
    !state.noteEncryption.hasLegacyIOSNotes &&
    !state.passphraseSetupDismissed
  ) {
    morph(root, renderPassphraseOnboarding());
    return;
  }
  morph(root, renderMainShell());
}
